// =============================================================================
// HidroVerse content — split from the legacy /lib/content.js.
// Imports continue to work from '../lib/content' via the barrel re-export.
// Edit copy here; do NOT touch the barrel in /lib/content.js.
// =============================================================================

// ==============================================================================
// TECHNOLOGY LIBRARY — factual, specification-led, industry-agnostic.
// Each entry is the source for /services/:slug/tech/:techSlug pages.
// Content sourced from public engineering literature and HidroVerse field experience.
// ==============================================================================

export const TECH_LIBRARY = {
  // ─── PURIFICATION ────────────────────────────────────────────────────
  "reverse-osmosis": {
    parent: "purification",
    name: "Reverse Osmosis (RO)",
    short: "When the dissolved load is the problem — RO is the answer.",
    whatItIs:
      "Reverse osmosis is a pressure-driven membrane process. Feedwater is pushed at 8–18 bar (brackish) or 55–80 bar (seawater) against a thin-film composite spiral-wound membrane; clean permeate passes through, and the dissolved load is concentrated into a reject (brine) stream.",
    whatItDoes: [
      "Rejects 95–99.7% of dissolved salts (Na⁺, Cl⁻, SO₄²⁻).",
      "Removes hardness (Ca²⁺, Mg²⁺), nitrate, fluoride, sulphate.",
      "Removes heavy metals — lead, arsenic, cadmium, uranium — to ppb levels.",
      "Removes most small organics and pesticides above ~150 Da.",
      "Produces low-TDS permeate suitable for potable, beverage and high-purity industrial duty.",
    ],
    whereItShines: [
      "Brackish boreholes with feed TDS in the multi-thousand mg/L range.",
      "Coastal sites where municipal pressure fails and groundwater is saline.",
      "Sites with non-negotiable potable-water targets — lodges, hospitals, food and beverage, pharma.",
      "Feeds where ion exchange or media filtration alone cannot reach SANS 241.",
    ],
    whereItDoesnt: [
      "Not a primary barrier against suspended solids — that is what pre-treatment is for.",
      "Not a true disinfection step on its own — bacterial barrier yes, validated disinfection no. UV after RO is the conservative answer.",
      "Less effective on volatile organics (THMs, light VOCs) — activated carbon is the right tool there.",
      "Dissolved gases (CO₂, H₂S, O₂) pass through and need degassing or pH correction afterward.",
      "Not the right answer for low-TDS surface water — UF and media-led trains cost less and last longer.",
    ],
    treatmentChain: {
      intro:
        "RO is never a standalone product. It is one stage inside a treatment train, and an RO plant that ships without engineered pre-treatment and post-treatment will fail — often within the first 12 months. Every HidroVerse RO install is designed end-to-end against the actual feed and the actual duty.",
      preTreatment: {
        label: "Pre-treatment",
        body:
          "Cartridge or media filtration to bring SDI₁₅ below 5; antiscalant dosing matched to the feed-water Langelier or Stiff & Davis index; chlorine removal (activated carbon or sodium metabisulphite) to protect the polyamide membrane; UF as a guaranteed physical barrier on dirtier feeds. Inadequate pre-treatment is the single largest cause of premature membrane failure.",
      },
      core: {
        label: "RO core",
        body:
          "Spiral-wound TFC membranes from tier-one OEMs (DuPont FilmTec, Toray, LG Chem, Hydranautics, Lanxess), array configuration sized against feed TDS, target recovery, fouling risk and the energy economics of the site. Energy-recovery devices on seawater and high-salinity brackish duties.",
      },
      postTreatment: {
        label: "Post-treatment",
        body:
          "RO permeate is very low TDS and typically slightly acidic — left untreated, it is corrosive to copper, steel and cementitious materials. We re-mineralise via calcite/dolomite contactor or precision dosing of calcium hydroxide and CO₂, lifting hardness to 60–80 mg/L as CaCO₃ and stabilising pH around 7.5–8.0 (positive LSI / RSI). Blending with a controlled fraction of pre-treated feed is sometimes the cheaper route. Final UV disinfection is standard on potable duty.",
      },
      brine: {
        label: "Brine management",
        body:
          "Reject at 50–75% recovery is roughly 2–4× the feed TDS. Disposal is site-specific — consented sewer discharge, evaporation pond, blended discharge with treated effluent, or concentrate recovery (zero-liquid-discharge) on high-value or environmentally sensitive sites. The route is part of the design pack and signed off against the water-use authorisation.",
      },
    },
    applications: [
      "Brackish-borehole drinking water polishing",
      "Coastal and seawater desalination",
      "Industrial process water and boiler feed",
      "Bottling, beverage and food-grade water",
      "Pharmaceutical and laboratory ultra-pure water",
      "Cooling-tower make-up water",
    ],
    whereWeUseIt:
      "RO is not a one-size-fits-all. We deploy it only where the dissolved load — TDS, hardness, iron, manganese, nitrate, fluoride, chloride, sulphate — cannot be addressed by physical or biological filtration alone, and only after a SANAS-accredited feed-water panel proves the case. On softer surface feeds we will recommend UF and media-led trains instead, because the lifecycle cost and reliability are better. When RO is the right answer, it is always paired with engineered pre-treatment and post-treatment as a single integrated plant — never as a standalone skid bolted onto a borehole pump.",
    related: ["ultrafiltration", "nanofiltration", "desalination"],
  },
  ultrafiltration: {
    parent: "purification",
    name: "Ultrafiltration (UF)",
    short: "A 0.01-µm wall that no bacterium, virus or cyst can cross.",
    whatItIs: "Ultrafiltration is a low-pressure (typically 0.5–2 bar) membrane process. Feedwater passes through hollow-fibre membranes with a nominal pore size of 0.01–0.02 µm; clean permeate passes through, suspended solids and microorganisms are rejected and back-flushed away on a defined cycle. UF is a physical barrier — not a chemical or biological process — and its integrity is verifiable.",
    whatItDoes: [
      "Delivers ≥4-log removal of bacteria, viruses and protozoan cysts (Cryptosporidium, Giardia) on validated membranes.",
      "Removes suspended solids, turbidity and colloidal particulates to below 0.1 NTU.",
      "Reduces SDI₁₅ to well below 5, ideal pre-treatment for RO.",
      "Integrity is provable on demand via pressure-decay test per USEPA Membrane Filtration Guidance Manual.",
      "Operates at low pressure with low energy intensity (typically <0.5 kWh/m³).",
    ],
    whereItShines: [
      "Surface water (river, dam, storm) where pathogen removal is non-negotiable.",
      "RO pre-treatment on heavy-fouling feeds where SDI₁₅ must be controlled.",
      "Sites that need a validated physical barrier downstream of a clarifier.",
      "Re-use applications polishing biological-plant effluent before disinfection.",
    ],
    whereItDoesnt: [
      "Not effective on dissolved load — salts, hardness, metals, organics pass straight through.",
      "Performance still relies on appropriate pre-treatment to manage fibre fouling.",
      "Fibre breakage is a real failure mode — integrity testing on schedule is non-negotiable.",
      "Not the right answer for low-turbidity, low-pathogen feeds where simpler stages suffice.",
    ],
    treatmentChain: {
      intro: "Ultrafiltration is one stage in a multi-barrier train — never a standalone product.",
      stages: [
      { label: "Pre-treatment", body: "Coarse screening (cyclone or self-cleaning screen), lamella clarification on heavy turbidity, chlorine removal where the feed is chlorinated." },
      { label: "Core stage", body: "Hollow-fibre UF modules at 0.01–0.02 µm with engineered TMP, back-flush and chemically-enhanced backwash (CEB) cycles." },
      { label: "Post-treatment", body: "RO or media polishing where dissolved load is the next concern; UV disinfection on potable duty; chlorine residual for distribution." },
      { label: "Waste handling", body: "Back-flush water routed to a settling pond or back to the head of works; CEB chemistry collected and neutralised before discharge." },
      ],
    },
    applications: [
      "Drinking-water polishing",
      "RO pre-treatment",
      "Wastewater tertiary polishing",
      "Beverage and food-grade filtration",
    ],
    whereWeUseIt: "We deploy UF wherever a validated physical barrier is required — particularly on surface-water inputs and ahead of RO on heavy-fouling feeds. Pre-treatment, CIP regime, integrity-test schedule and replacement-fibre budget are part of the engineered design.",
    related: [
      "reverse-osmosis",
      "nanofiltration",
      "media-filtration",
    ],
    },
  nanofiltration: {
    parent: "purification",
    name: "Nanofiltration (NF)",
    short: "The middle ground — hardness and colour out, energy cost down.",
    whatItIs: "Nanofiltration uses spiral-wound membranes with a molecular-weight cut-off of approximately 200–400 Da. NF rejects divalent ions (Ca²⁺, Mg²⁺, SO₄²⁻) at 85–99%, passes a portion of monovalent ions, and removes natural organic matter and tannins — at operating pressures and energy costs well below full RO.",
    whatItDoes: [
      "Removes hardness (Ca²⁺, Mg²⁺) at 85–99% rejection.",
      "Removes natural colour, tannins and dissolved organic carbon.",
      "Reduces sulphate, fluoride and other divalent species.",
      "Lower energy consumption than RO (typically 0.3–0.8 kWh/m³).",
      "Selectively passes some monovalent ions, retaining beneficial mineralisation.",
    ],
    whereItShines: [
      "Tea-coloured river and dam feeds where colour and organics dominate.",
      "Hardness reduction without full demineralisation.",
      "Sulphate removal on agricultural or municipal feeds.",
      "Sites where RO's energy cost cannot be justified by the dissolved load.",
    ],
    whereItDoesnt: [
      "Not effective on high-TDS or seawater feeds — full RO is required.",
      "Sodium and chloride pass through largely unrejected.",
      "Membrane fouling profile demands proper pre-treatment, just like RO.",
      "Application requires careful design — wrong membrane choice undersizes or overspends.",
    ],
    treatmentChain: {
      intro: "Nanofiltration is one stage in a multi-barrier train — pre-treatment and post-treatment are non-optional.",
      stages: [
      { label: "Pre-treatment", body: "Cartridge or media filtration to bring SDI₁₅ below 5; antiscalant dosing matched to the feed-water saturation indices; chlorine removal where required." },
      { label: "Core stage", body: "Spiral-wound NF membranes at 5–10 bar feed pressure with engineered recovery and concentrate management." },
      { label: "Post-treatment", body: "Re-mineralisation only where the brief demands it; disinfection (UV or chlorine) downstream of the membrane." },
      { label: "Waste handling", body: "Concentrate disposed under consented discharge; spent membranes returned to manufacturer recycler." },
      ],
    },
    applications: [
      "Hardness and colour removal",
      "Tannin and DOC reduction",
      "Sulphate removal",
      "Selective ion removal between UF and RO",
    ],
    whereWeUseIt: "We deploy NF where the brief is hardness, colour or organics — not full demineralisation. Membrane selection, recovery and array configuration are designed against the feed and the target water quality.",
    related: [
      "reverse-osmosis",
      "ultrafiltration",
      "ion-exchange",
    ],
    },
  "lamella-clarifier": {
    parent: "purification",
    name: "Lamella & Clarifier Systems",
    short: "Without it, your membranes fail in months instead of years.",
    whatItIs:
      "A lamella clarifier is a coagulation–flocculation–settling stage that uses a stack of inclined parallel plates. The plate stack multiplies the effective settling area inside a compact footprint (typically 5–10× a conventional open settling tank), so a small skid handles flows that would otherwise need an open pond.",
    whatItDoes: [
      "Removes suspended solids, silt, clay and colloidal matter to single-digit NTU on a well-dosed feed.",
      "Removes natural colour, tannins and a large fraction of dissolved organic carbon when paired with the right coagulant.",
      "Precipitates iron and manganese after aeration or oxidant dosing.",
      "Removes phosphorus by metal-salt precipitation in tertiary wastewater polishing.",
      "Reduces the load on every downstream membrane, media bed and disinfection stage.",
    ],
    whereItShines: [
      "River, dam and storm-water intakes with seasonal turbidity spikes.",
      "Municipal raw-water plants and small-town potable schemes.",
      "Sites where membrane plants are failing prematurely because pre-treatment was undersized.",
      "Wastewater tertiary polishing for phosphorus and residual solids.",
    ],
    whereItDoesnt: [
      "Not a disinfection step — pathogens pass straight through unless paired with downstream UV, ozone or chlorination.",
      "Not the right tool for dissolved salts, heavy metals (in dissolved form) or hardness — those need ion exchange or RO.",
      "Performance collapses without correct coagulant chemistry and dosing; it is an engineered process, not a tank.",
      "Footprint and head loss demands the plant be designed in at master-planning stage, not retrofitted under a deck.",
    ],
    treatmentChain: {
      intro: "Clarification is never the whole train. It is a primary stage that conditions the water for everything downstream.",
      stages: [
      { label: "Pre-treatment", body: "Coarse screening and grit removal (hydrocyclone or self-cleaning screen) to protect the chemical-dosing skid and the plate stack from rags, leaves and large particulates." },
      { label: "Core stage", body: "Coagulation (PAC, alum or ferric chloride dosed against jar-test results) and flocculation, followed by gravity settling between inclined plates. Clarified water collects above; sludge slides into the hopper." },
      { label: "Post-treatment", body: "Multi-media filtration, GAC polish and disinfection (UV or chlorination) downstream against the final-water target — clarification alone is not a finished product." },
      { label: "Waste handling", body: "Sludge is wasted on a controlled schedule, dewatered (drying beds or geotextile bags) and disposed of as classified waste under manifest." },
      ],
    },
    applications: [
      "Pre-treatment for membrane plants on river/dam/storm-water feeds",
      "Removal of silt, clay, colour, turbidity and biological load",
      "Phosphorus precipitation in wastewater tertiary polishing",
      "Iron and manganese removal after aeration",
      "Municipal raw-water treatment",
    ],
    whereWeUseIt:
      "We deploy lamella clarification anywhere the feed water carries enough suspended or colloidal load to compromise downstream stages. The decision is led by the analytical data — turbidity, total suspended solids, true colour, dissolved organic carbon — and by jar-testing in our lab against the actual coagulant chemistry the site will use. We never bolt a clarifier onto a train as an afterthought; it is sized at design stage, integrated into the hydraulic profile, and matched with the right coagulant, flocculant and dosing strategy for the source.",
    related: ["sand-separators", "media-filtration"],
  },
  "sand-separators": {
    parent: "purification",
    name: "Sand Separators & Screens",
    short: "The cheapest insurance policy you'll ever put on a pump.",
    whatItIs:
      "Hydrocyclone separators and self-cleaning screens are mechanical first-line barriers. The hydrocyclone uses tangential inlet flow to spin heavier particles outward by centrifugal force; the self-cleaning screen carries a fine mesh that back-flushes automatically when differential pressure crosses a threshold. Both are passive in chemistry and minimal in power.",
    whatItDoes: [
      "Removes approximately 98% of particles larger than ~74 µm from borehole, river and dam intakes.",
      "Protects pumps, valves, fittings and downstream media beds from abrasive damage.",
      "Removes leaves, fibrous debris and biological mats from open-water intakes.",
      "Operates continuously with automatic flush — no consumables, no operator intervention.",
    ],
    whereItShines: [
      "Borehole intakes where the screen draws sand or silt during pump start-up.",
      "River, dam and storm-water intakes where organic debris and grit are seasonal.",
      "Irrigation feed lines protecting drip emitters and pop-up heads.",
      "First-stage protection for every downstream membrane, media bed and dosing skid.",
    ],
    whereItDoesnt: [
      "Not a barrier against dissolved contaminants (salts, hardness, heavy metals, organics).",
      "Not a fine-particulate stage — sub-micron solids and colloids pass straight through.",
      "Not a disinfection step — bacteria and viruses are unaffected.",
      "Without correct sizing for the feed flow, particles bypass the cyclone or blind the screen prematurely.",
    ],
    treatmentChain: {
      intro: "Separators and screens are always a first-stage protection layer — never a treatment in their own right.",
      stages: [
      { label: "Pre-treatment", body: "Inlet piping, isolation valving and a flow-conditioning section ahead of the cyclone or screen so the device sees the flow it was sized for." },
      { label: "Core stage", body: "Hydrocyclone (heavy fraction) or self-cleaning screen (organic and fibrous fraction). On dirty river or storm intakes we routinely deploy both in series." },
      { label: "Post-treatment", body: "The full purification train downstream — clarification, media, membranes, disinfection. The separator buys their life, it does not replace any of them." },
      { label: "Waste handling", body: "Cyclone purge and screen back-flush volumes are routed to a controlled discharge — pond, sediment trap or back to head of works for re-treatment." },
      ],
    },
    applications: [
      "Borehole grit removal",
      "River-intake pre-screening",
      "Pre-protection of pumps, valves and membranes",
      "Irrigation feed screening",
    ],
    whereWeUseIt:
      "We specify a separator or self-cleaning screen as the first stage of nearly every borehole, river or storm-fed train. The technology is cheap to install, consumes nothing in operation and pays for itself many times over by extending the life of every component downstream of it. Sizing is led by the feed flow, the expected particle distribution and the pressure budget — not by a single-line catalogue selection.",
    related: ["lamella-clarifier", "media-filtration"],
  },
  "media-filtration": {
    parent: "purification",
    name: "Multi-Media & Activated Carbon Filtration",
    short: "The workhorse depth-filtration stage between pre-treatment and membrane.",
    whatItIs:
      "A multi-media bed is a pressure vessel layered with progressively finer media — typically anthracite, silica sand and garnet — at filtration rates of 8–12 m/h. Activated carbon (GAC) is run as a separate vessel or stage that adsorbs dissolved organics, residual chlorine, taste, odour and trihalomethanes. Both run on automatic backwash cycles.",
    whatItDoes: [
      "Removes turbidity and suspended solids down to roughly 10–20 µm in a single pass.",
      "Removes iron and manganese in oxidised form (with appropriate upstream aeration or oxidant).",
      "Removes free chlorine, chloramines, taste and odour at the GAC stage.",
      "Reduces dissolved organic carbon, trihalomethanes and many low-molecular-weight pollutants on GAC.",
      "Self-cleans on a scheduled backwash — no operator intervention between cycles.",
    ],
    whereItShines: [
      "Pre-treatment ahead of RO, UF or UV on borehole, river and municipal feeds.",
      "Sites where membrane fouling is driving early replacements.",
      "Iron and manganese removal in groundwater treatment.",
      "Taste, odour and chlorine polishing on municipal-fed lodges, hotels and food-grade applications.",
    ],
    whereItDoesnt: [
      "Not a disinfection step — pathogens pass through unless paired with downstream UV, ozone or chlorination.",
      "Not effective on dissolved salts, hardness or most heavy metals — those need RO or ion exchange.",
      "GAC capacity is finite — exhausted carbon stops adsorbing and must be regenerated or replaced on schedule.",
      "Performance depends on correct media grading, bed depth and filtration velocity — under-sized beds short-circuit and over-foul.",
    ],
    treatmentChain: {
      intro: "Multi-media and activated carbon are intermediate stages — sized against what comes in and what must follow.",
      stages: [
      { label: "Pre-treatment", body: "Coarse separation (screens or cyclones) and, on heavy turbidity, lamella clarification — so the bed does not blind in days instead of months." },
      { label: "Core stage", body: "Multi-media depth filtration (anthracite/silica/garnet) for solids polish, followed by activated carbon (GAC) for chlorine, taste, odour and dissolved organics." },
      { label: "Post-treatment", body: "Membrane stage (UF/RO) or disinfection (UV/chlorine) — multi-media is rarely the final stage for potable duty on its own." },
      { label: "Waste handling", body: "Scheduled backwash water carries the trapped solids to a settling pond or back to the head of the works. GAC is regenerated or replaced when adsorption capacity is exhausted." },
      ],
    },
    applications: [
      "Turbidity reduction before membrane plants",
      "Chlorine, taste and odour removal (carbon stage)",
      "Iron and manganese removal (with greensand or oxidation pre-treatment)",
      "Final polish of treated water before storage",
    ],
    whereWeUseIt:
      "Almost every HidroVerse train carries a multi-media stage and a GAC polish. They are invisible to the user and indispensable to the engineer. The grading, vessel sizing and backwash strategy are designed against the actual feed-water analysis — we do not buy media beds from a catalogue and bolt them on.",
    related: ["lamella-clarifier", "ion-exchange"],
  },
  "ion-exchange": {
    parent: "purification",
    name: "Ion Exchange",
    short: "When the problem is one ion — not the whole TDS profile.",
    whatItIs:
      "An ion-exchange column is a pressure vessel packed with resin beads carrying exchangeable ions. As feedwater passes through, the resin swaps its loaded ions for the target ions in the water. Strong-acid cation (SAC) resin softens water by exchanging Ca²⁺ and Mg²⁺ for Na⁺. Strong-base anion (SBA) and selective resins target nitrate, fluoride, arsenic, uranium or natural organic matter. Resin is periodically regenerated with brine (softeners) or acid + caustic (demineralisers).",
    whatItDoes: [
      "Softens water by removing calcium and magnesium hardness to < 5 mg/L as CaCO₃.",
      "Selectively removes nitrate, fluoride, arsenic or uranium below SANS 241 limits.",
      "Demineralises water to conductivity well below 1 µS/cm for boiler feed, lab and pharma duty.",
      "Removes natural organic matter on macroporous anion resins as RO pre-treatment.",
      "Targets specific heavy metals using chelating resins where RO is over-spent.",
    ],
    whereItShines: [
      "Hard borehole and municipal feeds where appliance scaling is the dominant problem.",
      "Rural and agricultural sites with high nitrate or fluoride in groundwater.",
      "Boiler, steam and laboratory applications that demand truly demineralised water.",
      "Sites where one or two ions need to be removed without de-mineralising the whole supply.",
    ],
    whereItDoesnt: [
      "Not a disinfection step — bacteria and viruses pass through and the resin bed can itself harbour biofilm.",
      "Not effective on suspended solids, turbidity or colloids — pre-treatment is mandatory.",
      "Regeneration produces a concentrated brine or acid/caustic waste stream that needs a disposal route and a consent.",
      "Not the right tool when the entire dissolved load (TDS) needs to be cut — that is what RO does, more economically.",
    ],
    treatmentChain: {
      intro: "Ion exchange is a targeted stage. The pre-treatment that protects the resin and the regeneration that resets it are as important as the bed itself.",
      stages: [
      { label: "Pre-treatment", body: "Particulate filtration to prevent resin fouling; chlorine removal where chlorinated feedwater would oxidise the resin; iron and manganese removal where they would foul the bed." },
      { label: "Core stage", body: "Resin column selected for duty — strong-acid cation for softening, strong-base anion or selective resin for nitrate/fluoride/arsenic/uranium, chelating resin for specific heavy metals." },
      { label: "Post-treatment", body: "Polishing filter to capture any resin fines; pH correction if the bed has shifted the buffer; downstream disinfection." },
      { label: "Waste handling", body: "Regeneration brine (softeners) or acid + caustic streams (demineralisers) need neutralisation, controlled discharge, and a consented disposal route." },
      ],
    },
    applications: [
      "Water softening for hard borehole and municipal water",
      "Demineralisation for boiler feed and lab water",
      "Nitrate removal in agricultural and rural drinking water",
      "Fluoride and arsenic removal with specific resins",
      "Selective heavy-metal removal with chelating resins",
    ],
    whereWeUseIt:
      "We deploy ion exchange when a specific ion needs to come out — and only that ion. Softening is the classic application; nitrate and fluoride removal are the others we see most often on rural drinking-water schemes. Selection of the resin chemistry (SAC, SBA, weak-acid, chelating) is led by the analytical data and the operating economics. We engineer regeneration chemistry, brine handling and disposal route into the design pack, not as an afterthought.",
    related: ["reverse-osmosis", "media-filtration"],
  },
  "uv-disinfection": {
    parent: "purification",
    name: "UV Disinfection",
    short: "The cleanest final-step kill on the market — no chemistry, no residual, no by-products.",
    whatItIs:
      "UV disinfection uses ultraviolet light in the UV-C band (peak germicidal output at 254 nm from low-pressure mercury lamps) to damage the DNA of microorganisms in flowing water. Effective dose is measured in mJ/cm² and is the product of UV intensity and contact time. For potable applications we typically design to ≥40 mJ/cm² (NSF/ANSI 55 Class A), delivering ≥4-log inactivation of bacteria and viruses and validated inactivation of Cryptosporidium and Giardia at substantially lower doses.",
    whatItDoes: [
      "Inactivates bacteria (E. coli, coliforms, Legionella) at validated doses of 20–40 mJ/cm².",
      "Inactivates viruses including enteric viruses at the same dose range.",
      "Inactivates protozoan cysts — Cryptosporidium and Giardia — at much lower doses (typically 4–12 mJ/cm²).",
      "Leaves no chemical residual, no taste change, no disinfection by-products.",
      "Continuous, automatic operation with logged intensity and lamp-failure alarms.",
    ],
    whereItShines: [
      "Final-step disinfection on every potable train, after RO, UF or media filtration.",
      "Sites that cannot accept chlorine taste, odour or disinfection by-products (food, beverage, hospitality).",
      "Wastewater tertiary disinfection before discharge or reuse.",
      "Aquaculture, hydroponics and food-processing applications.",
    ],
    whereItDoesnt: [
      "Not a removal technology — UV does not take anything out of the water; it inactivates pathogens only.",
      "Not effective if upstream water is turbid — suspended solids shield organisms from the UV dose.",
      "Not a residual disinfectant — protection ends at the reactor outlet, so distribution lines need a residual chlorine or chloramine if recontamination risk is present.",
      "Lamp ageing and quartz-sleeve fouling progressively reduce dose — without intensity monitoring, a UV plant fails silently.",
    ],
    treatmentChain: {
      intro: "UV almost always sits as a final or near-final stage. Everything upstream must deliver clear, low-UVT water; everything downstream depends on the kill it delivers.",
      stages: [
      { label: "Pre-treatment", body: "The full filtration train — sediment, media, sometimes UF or RO — to deliver feedwater below 1 NTU and with UV transmittance above 85% at 254 nm." },
      { label: "Core stage", body: "Validated UV reactor sized to ≥40 mJ/cm² at the worst-case feed transmittance; continuous intensity monitoring; quartz-sleeve cleaning regime; lamp-failure alarm interlocked to fail-safe valve on critical lines." },
      { label: "Post-treatment", body: "On distribution networks with recontamination risk, a small residual chlorine dose preserves protection between reactor and tap." },
      { label: "Waste handling", body: "Quartz-sleeve cleaning chemistry is collected and disposed; spent UV lamps contain mercury and are returned to a registered recycler." },
      ],
    },
    applications: [
      "Final-step disinfection of drinking water",
      "Wastewater tertiary disinfection before discharge or reuse",
      "Pool and spa water treatment (with chlorine residual)",
      "Bottling and beverage water",
      "Aquaculture and hydroponics",
    ],
    whereWeUseIt:
      "Almost every potable train HidroVerse signs ends with a UV stage. It is the defensible, no-residual, no-by-products final barrier — sized to ≥40 mJ/cm² on the worst-case feed transmittance, with continuous intensity monitoring, lamp-failure alarms and a quartz-sleeve cleaning schedule built into the maintenance contract. We pair UV with a small residual chlorine dose only where the distribution network creates a recontamination risk.",
    related: ["ozone", "chlorination"],
  },
  ozone: {
    parent: "purification",
    name: "Ozone Treatment",
    short: "When chlorine and UV together can't deliver the guest-grade taste, colour and odour the brief demands.",
    whatItIs:
      "Ozone is generated on-site from oxygen or dry air using high-voltage corona-discharge cells, then dissolved into the water in a contact chamber. It oxidises microorganisms and organic compounds and decomposes back to oxygen within minutes, leaving no residual. CT (concentration × time) is engineered against the relevant disinfection rule for the application.",
    whatItDoes: [
      "Inactivates bacteria, viruses and protozoan cysts (Cryptosporidium and Giardia) at validated CT values.",
      "Oxidises iron, manganese and hydrogen sulphide ahead of media filtration.",
      "Removes colour, taste and odour compounds — including geosmin and MIB — that chlorine cannot touch.",
      "Pre-oxidises dissolved organic carbon ahead of activated carbon for deeper polish.",
      "Leaves no chemical residual in the treated water.",
    ],
    whereItShines: [
      "Premium lodges and food/beverage sites where guest-perceived water quality must be exceptional.",
      "Surface-water plants with persistent taste, colour or organic problems.",
      "Pool and spa hybrid systems looking to cut chemical load and disinfection by-products.",
      "Wastewater tertiary disinfection where bromate formation can be controlled.",
    ],
    whereItDoesnt: [
      "Not a distribution residual — protection ends at the contact-chamber outlet; a small chlorine dose is added for long pipe networks.",
      "Capital and energy cost is meaningful — over-spent on simple, clean feeds where UV alone is enough.",
      "Off-gas destructors and safety interlocks are mandatory; the plant cannot be casually operated.",
      "Bromide-bearing waters can form bromate (a regulated disinfection by-product) — feed chemistry must be checked first.",
    ],
        treatmentChain: {
      intro: "Ozone is a powerful oxidiser and a real engineered process — generator, contactor, off-gas destruction and safety interlocks are all part of the package.",
      stages: [
      { label: "Pre-treatment", body: "Particulate removal upstream so the contactor is not loaded by suspended matter. Bromide screening on the feed — bromate formation is the risk to manage." },
      { label: "Core stage", body: "On-site ozone generation from oxygen or dry air; controlled injection into a contact chamber sized for the CT target; ORP feedback control on dosing." },
      { label: "Post-treatment", body: "Off-gas destructor on the contactor headspace; activated-carbon polishing where ozone has pre-oxidised dissolved organics; small chlorine residual on distribution." },
      { label: "Waste handling", body: "Off-gas is destroyed (thermal or catalytic) before atmospheric release; ozone process is closed-loop within the reactor — no waste stream to the environment." },
      ],
    },
    applications: [
      "Taste, odour and colour polishing of drinking water",
      "Manganese, iron and sulphide oxidation",
      "Pool and spa disinfection (low-chemical hybrid systems)",
      "Pre-treatment to activated carbon for organics removal",
      "Wastewater tertiary disinfection",
    ],
    whereWeUseIt:
      "We deploy ozone where chlorine and UV together cannot deliver the guest-grade taste, colour and odour the brief demands. Generator sizing, contact-chamber design, off-gas destruction and CT compliance are part of the engineered package — never a bolt-on cartridge.",
    related: ["uv-disinfection", "chlorination"],
  },
  chlorination: {
    parent: "purification",
    name: "Chlorination",
    short: "The residual that keeps water safe between the plant and the tap.",
    whatItIs:
      "Chlorination dosing — as sodium hypochlorite, calcium hypochlorite, chlorine gas, or generated on-site by electrolysis of brine — oxidises microorganisms and leaves a measurable free-chlorine residual that protects water as it travels from plant to tap. Dosing is calibrated to feed flow with ORP or amperometric feedback control.",
    whatItDoes: [
      "Inactivates bacteria and viruses at correctly calculated CT values.",
      "Maintains a distribution-network residual of 0.2–0.5 mg/L free chlorine, protecting against recontamination.",
      "Provides shock disinfection of tanks, lines and biofilm during commissioning or incident recovery.",
      "Oxidises iron, manganese and some organics as a low-cost pre-treatment.",
    ],
    whereItShines: [
      "Long lodge, estate or municipal reticulation networks where recontamination risk is real.",
      "Storage-tank-fed systems where contact time downstream of the plant is unavoidable.",
      "Emergency response — boil-water notices, tank cleaning, line flushing.",
      "Sites where a measurable residual is required by regulation or insurer.",
    ],
    whereItDoesnt: [
      "Not effective against Cryptosporidium and other chlorine-tolerant protozoa — pair with UV or ozone.",
      "Reacts with natural organic matter to form regulated disinfection by-products (THMs, HAAs) on dirty feeds.",
      "Can produce taste and odour issues that defeat the brief on premium hospitality sites — pair with GAC or use UV/ozone primary instead.",
      "Inhalation and handling risk on gas dosing — modern installs default to hypochlorite or on-site generation.",
    ],
        treatmentChain: {
      intro: "Chlorination is the layered protection on distribution networks — never the only barrier on a potable train.",
      stages: [
      { label: "Pre-treatment", body: "The full purification train ahead of chlorine — sediment, media, membranes, sometimes UV. Chlorinating dirty water just creates disinfection by-products." },
      { label: "Core stage", body: "Sodium hypochlorite dosing (or on-site brine electrolysis) with ORP or amperometric feedback control on the network residual." },
      { label: "Post-treatment", body: "Residual monitoring at distribution end-points; periodic shock dosing on storage tanks; THM and HAA sampling on long networks." },
      { label: "Waste handling", body: "Dosing-chemistry containment and bunding; emergency neutralisation kit on site; expired stock returned via licensed chemical-waste contractor." },
      ],
    },
    applications: [
      "Distribution-system residual disinfection",
      "Municipal water treatment",
      "Pool and spa primary disinfection",
      "Emergency / shock disinfection of tanks and lines",
    ],
    whereWeUseIt:
      "Chlorination is the layer we add when a distribution residual is required — long pipe networks, estate reticulation, municipal mains, storage-fed systems. We dose with feedback control on residual, not on flow alone, and we pair with UV or ozone as the primary kill step on sensitive applications.",
    related: ["uv-disinfection", "ozone"],
  },
  desalination: {
    parent: "purification",
    name: "Desalination (Seawater RO)",
    short: "When the only source is salt — and tankering is not an option.",
    whatItIs:
      "Seawater desalination is reverse osmosis sized for very high feed-water salinity. Pre-treated seawater (typically ~35 000 mg/L TDS) is pumped at 55–80 bar across high-rejection thin-film composite membranes; permeate passes through, the concentrate is depressurised through an energy-recovery device (ERD) that returns most of its pressure energy back into the feed pump.",
    whatItDoes: [
      "Rejects 99.4–99.8% of dissolved salts from seawater feeds.",
      "Delivers potable-grade permeate (TDS typically <500 mg/L) ready for re-mineralisation and distribution.",
      "Energy-recovery devices recover up to 60% of the feed-pump energy from the concentrate stream.",
      "Designed for continuous duty in marine bio-fouling and high-chloride conditions.",
    ],
    whereItShines: [
      "Coastal lodges and resorts with no usable freshwater source.",
      "Island and offshore installations dependent on the sea for supply.",
      "Coastal industrial sites where rainwater alone cannot cover demand peaks.",
      "Marine vessels and offshore platforms.",
    ],
    whereItDoesnt: [
      "Wrong tool for low-TDS brackish water — over-pressurised and over-spent.",
      "Energy cost is substantial — 2.5–4.0 kWh/m³ even with modern ERDs; project economics require honest modelling.",
      "Concentrate disposal back to the marine environment needs an environmental authorisation and diffuser design.",
      "Marine bio-fouling demands rigorous pre-treatment — UF and dosing chemistry that cost more than brackish systems.",
    ],
        treatmentChain: {
      intro: "Seawater desalination is a high-engineered package — intake, pre-treatment, RO core, ERD, post-treatment and concentrate disposal are designed as one system.",
      stages: [
      { label: "Pre-treatment", body: "Coastal intake (beach well, open sea or HDD), chlorination, coagulation, multi-media or UF to bring SDI₁₅ below 5 on the membrane feed." },
      { label: "Core stage", body: "High-pressure (55–80 bar) seawater RO with high-rejection thin-film membranes; energy-recovery devices on the concentrate side; chlorine removal upstream of the membrane." },
      { label: "Post-treatment", body: "Re-mineralisation (calcite or dolomite contactor) to lift hardness and stabilise pH; UV disinfection; chlorine residual for distribution." },
      { label: "Waste handling", body: "Concentrate diffuser discharge under marine environmental authorisation, designed to dilute brine in the receiving water within a defined zone of impact." },
      ],
    },
    applications: [
      "Coastal town and lodge supply",
      "Island resort water security",
      "Marine vessel and offshore platforms",
      "Industrial coastal sites",
    ],
    whereWeUseIt:
      "Desalination is the answer when the only available source is salt. We deploy it on coastal lodges, island resorts and remote marine sites where tankering is the alternative. Pre-treatment (UF, dosing), the high-pressure stage, the ERD selection and the marine concentrate diffuser are designed as one integrated package, not bought from separate suppliers.",
    related: ["reverse-osmosis", "ultrafiltration"],
  },

  // ─── WASTEWATER ──────────────────────────────────────────────────────
  "fat-grease-traps": {
    parent: "wastewater",
    name: "Fat Traps & Grease Arrestors",
    short: "What the kitchen does not catch, the biological plant cannot survive.",
    whatItIs:
      "A fat-trap or grease arrestor is a primary interception chamber sized for at least 30–60 minutes of hydraulic retention. Kitchen wastewater enters; fats and oils rise to the surface, heavier food solids settle to the bottom, clarified middle-water passes onward. Scheduled pump-outs remove the accumulated grease cake before it can overflow.",
    whatItDoes: [
      "Intercepts fats, oils and greases (FOG) before they enter the sewer or biological plant.",
      "Captures food solids that would otherwise blind downstream screens and pumps.",
      "Prevents pipe blockages, blower failures and biological-reactor upsets.",
      "Provides a controlled removal point for scheduled FOG disposal under waste manifest.",
    ],
    whereItShines: [
      "Commercial kitchens — hotels, lodges, conference venues, restaurants, food halls.",
      "Food-processing facilities discharging to a biological treatment plant.",
      "Sites discharging to sewer where the local authority enforces FOG limits.",
      "Anywhere kitchen flow joins black-water before treatment.",
    ],
    whereItDoesnt: [
      "Not a treatment step — FOG is intercepted, not destroyed; pump-out and disposal route are mandatory.",
      "Undersized or unmaintained traps fail silently and overflow downstream — schedule is non-optional.",
      "Not designed for non-kitchen wastewater (sanitary, laundry) — these need separate primary treatment.",
      "Does not address dissolved BOD or nutrients in kitchen wastewater.",
    ],
        treatmentChain: {
      intro: "A grease arrestor is one part of an integrated wastewater system — sized for the kitchen, maintained on schedule, manifested to a waste contractor.",
      stages: [
      { label: "Pre-treatment", body: "Coarse food-solids strainers at point-of-use sinks and dishwashers, with scheduled cleaning." },
      { label: "Core stage", body: "Sized FOG arrestor providing 30–60 minutes hydraulic retention; FOG floats, food solids settle, clarified middle-water passes onward." },
      { label: "Post-treatment", body: "The downstream wastewater train — biological reactor (MBBR, SBR, MBR), clarifier, tertiary polishing — receives feed conditioned to manageable FOG load." },
      { label: "Waste handling", body: "Scheduled FOG pump-out under waste manifest; disposal via licensed FOG-waste contractor with reconciliation against the manifest." },
      ],
    },
    applications: ["Commercial kitchen drainage", "Hotel & lodge food-prep areas", "Food-processing pre-treatment"],
    whereWeUseIt: "Upstream of every wastewater plant whose influent contains kitchen flow. We size the trap against the actual hydraulic and FOG load, specify pump-out frequency in the maintenance contract, and route disposal through a licensed FOG-waste contractor with manifest reconciliation.",
    related: ["septic-tanks", "screen-filters"],
  },
  "septic-tanks": {
    parent: "wastewater",
    name: "Septic Tanks & Settling Chambers",
    short: "Primary settlement done right — not a soak-away dressed up as a system.",
    whatItIs:
      "A septic tank is a sealed underground chamber providing quiescent primary settlement and partial anaerobic digestion. Raw sewage flows in; solids settle as sludge, fats and oils float as scum, clarified liquor flows on to secondary treatment. Anaerobic bacteria partially break down the settled sludge between scheduled pump-outs.",
    whatItDoes: [
      "Removes 60–70% of suspended solids and 30–40% of BOD as a primary stage.",
      "Provides hydraulic buffering of peak inlet flows ahead of secondary treatment.",
      "Stores partially-digested sludge between scheduled pump-out cycles.",
      "Functions without power, chemistry or operator intervention between visits.",
    ],
    whereItShines: [
      "Off-grid lodges, farms and remote residential sites.",
      "Primary settlement ahead of aerobic biological or membrane bioreactor stages.",
      "Sites where power supply is intermittent and gravity-led plants are essential.",
      "Low-flow rural drinking-water-services-act applications.",
    ],
    whereItDoesnt: [
      "Not a final treatment step on its own — effluent is not safe to discharge or reuse without secondary and tertiary stages.",
      "Soak-aways and French drains are not a treatment train — they are a disposal route, and a polluting one on most South African geologies.",
      "Undersized septics overflow solids into downstream stages and ruin them.",
      "Anaerobic conditions produce hydrogen sulphide, methane and odour — ventilation is a design requirement, not an option.",
    ],
        treatmentChain: {
      intro: "A septic tank is a primary stage. Treatment that holds up to a regulator demands secondary and tertiary stages downstream — never a soak-away.",
      stages: [
      { label: "Pre-treatment", body: "Coarse screening at the inlet to remove rags and gross solids that would short-cycle the tank." },
      { label: "Core stage", body: "Sealed multi-chamber septic settlement: solids settle as sludge, FOG floats as scum, clarified liquor flows forward. Anaerobic digestion stabilises sludge between pump-outs." },
      { label: "Post-treatment", body: "Aerobic biological treatment (MBBR/SBR/MBR) for secondary; UV or chlorine disinfection for tertiary; sized polish on reuse or discharge target." },
      { label: "Waste handling", body: "Scheduled honey-sucker pump-out of accumulated sludge; disposal to a licensed wastewater works under manifest." },
      ],
    },
    applications: ["Rural and remote site primary treatment", "Lodge and estate primary settlement", "Pre-treatment ahead of aerobic biological stages"],
    whereWeUseIt: "We deploy septic primary settlement on every off-grid or non-municipally-served site, sized against the actual occupancy and digestion-friendly residence times. We do not specify septic plus soak-away as a complete treatment solution — that is a regulatory and environmental failure waiting to happen.",
    related: ["fat-grease-traps", "mbbr"],
  },
  "screen-filters": {
    parent: "wastewater",
    name: "Screen Filters & Raw Effluent Separation",
    short: "First defence between the inlet and everything that costs real money downstream.",
    whatItIs:
      "Mechanical screens are the first physical stage in a wastewater train. Effluent passes through a screen of defined aperture (3–10 mm for fine screens, sub-1 mm for membrane-protection duty); captured solids are scraped, brushed or back-flushed into a discharge chute. Modern self-cleaning rotary designs run continuously with minimal operator intervention.",
    whatItDoes: [
      "Removes rags, hair, sanitary products, food fibres and gross solids from inlet flow.",
      "Protects pumps, blowers, valves and membrane modules from physical damage.",
      "Reduces solids load on biological reactors and clarifiers downstream.",
      "Continuous automatic operation with minimal labour requirement.",
    ],
    whereItShines: [
      "Inlet screening before any biological treatment stage.",
      "Membrane-bioreactor (MBR) installations where membrane protection is non-negotiable.",
      "Industrial-effluent pre-treatment with high gross-solids loading.",
      "Sites where dry-screen handling is required for waste manifest compliance.",
    ],
    whereItDoesnt: [
      "Not a treatment step — removes mechanical solids only, no BOD or nutrient reduction.",
      "Fine screens block on fibrous waste if mesh is incorrectly sized for the influent.",
      "Captured screenings are a Category 1 waste stream and need a proper disposal route.",
      "Not effective on grit and sand — pair with hydrocyclone or grit chamber upstream.",
    ],
        treatmentChain: {
      intro: "Inlet screening is a non-negotiable first stage on every wastewater plant we build.",
      stages: [
      { label: "Pre-treatment", body: "Grit chamber or hydrocyclone upstream where heavy sand is present in the influent." },
      { label: "Core stage", body: "Mechanical screen (typically 3–10 mm fine, sub-1 mm where MBR membranes need protecting) with automatic scraping or back-flushing." },
      { label: "Post-treatment", body: "FOG arrestor (if not handled at source); equalisation tank; biological reactor; clarifier; tertiary stages." },
      { label: "Waste handling", body: "Screenings are dewatered and disposed of as Category 1 waste under manifest — never returned to the head of the works." },
      ],
    },
    applications: ["Inlet screening before biological treatment", "Membrane-bioreactor protection", "Industrial-effluent pre-treatment"],
    whereWeUseIt: "Ahead of every biological reactor and every membrane stage we install. Screen aperture is matched to the protection duty downstream — fine screens for MBR, coarser bar screens for SBR and MBBR.",
    related: ["sand-separators", "membrane-bioreactor"],
  },
  "aerobic-biological": {
    parent: "wastewater",
    name: "Aerobic & Biological Reactors",
    short: "Where wastewater becomes water you can put back into the world.",
    whatItIs:
      "Aerobic biological reactors use a managed population of aerobic micro-organisms to consume dissolved organic carbon (BOD/COD) and convert ammoniacal nitrogen to nitrate. The two most common families — MBBR (moving-bed biofilm reactor) and SBR (sequencing batch reactor) — share the same biological mechanism but differ in vessel layout and process control.",
    whatItDoes: [
      "Removes 90–98% of BOD/COD on well-designed plants.",
      "Nitrifies ammonia to nitrate via Nitrosomonas and Nitrobacter populations.",
      "Settles biological floc to produce clarified effluent suitable for tertiary polishing.",
      "Can be configured for nitrogen removal with anoxic zones and recirculation.",
    ],
    whereItShines: [
      "Lodges, hotels, estates and small towns where decentralised secondary treatment is needed.",
      "Sites with variable hydraulic load (SBR is forgiving of intermittent flow).",
      "Sites requiring nitrification ahead of reuse or sensitive discharge.",
      "Retrofits where biological capacity must be added inside an existing tank footprint (MBBR).",
    ],
    whereItDoesnt: [
      "Not effective against most pharmaceutical, personal-care or industrial recalcitrant compounds.",
      "Performance is temperature-dependent — winter design margins are mandatory.",
      "Requires reliable power for aeration — outages stress the biology and recovery takes days.",
      "Cannot reach reuse-grade quality alone; tertiary stages (UF, UV, disinfection) are required for reuse.",
    ],
        treatmentChain: {
      intro: "Biological treatment is the secondary core of every wastewater plant — but only one stage in a sequence that starts with screening and ends with disinfection.",
      stages: [
      { label: "Pre-treatment", body: "Screening, grit removal, FOG arrestor and equalisation tank to deliver biologically-feedable wastewater at controlled flow." },
      { label: "Core stage", body: "Aerobic reactor (MBBR or SBR) with engineered aeration, dissolved-oxygen control and biomass management; anoxic zone where nitrogen removal is required." },
      { label: "Post-treatment", body: "Clarifier or membrane separation; disinfection (UV or chlorine); polishing filtration for reuse-grade quality." },
      { label: "Waste handling", body: "Waste activated sludge (WAS) dewatering and disposal; biosolids managed under DWS authorisation; non-conformant effluent diverted to recirculation, never released." },
      ],
    },
    applications: ["Municipal secondary treatment", "Lodge and resort biological treatment", "Industrial-effluent biological polishing"],
    whereWeUseIt: "Aerobic biological treatment is the workhorse secondary stage of almost every HidroVerse wastewater plant. MBBR for sites that need compact, retrofittable capacity; SBR for sites with strong batch flow profiles and stringent effluent targets. Tertiary polishing is layered on against the discharge or reuse target — not assumed away.",
    related: ["mbbr", "sbr", "activated-sludge"],
  },
  mbbr: {
    parent: "wastewater",
    name: "MBBR (Moving Bed Biofilm Reactor)",
    short: "Compact biological treatment that absorbs shocks and retrofits into existing tanks.",
    whatItIs: "MBBR (Moving Bed Biofilm Reactor) is an aerobic biological stage using free-floating plastic carriers as growth surface for biomass. Aeration keeps the carriers suspended and moving; biomass grows as a biofilm on the carrier surface; effluent passes onward to clarification or membrane separation. Biomass is retained in the reactor by mesh sieves on the outlet, not by a clarifier recycle.",
    whatItDoes: [
      "Removes 85–95% of BOD/COD on properly sized plants.",
      "Nitrifies ammonia to nitrate with sufficient retention time and SRT.",
      "Tolerates significant shock loads and intermittent flow.",
      "Compact footprint — carriers provide 200–500 m² of biomass surface area per m³ of reactor volume.",
      "Retrofits into existing tanks without major civil work.",
    ],
    whereItShines: [
      "Lodges and estates with variable occupancy and shock-load profiles.",
      "Brownfield retrofits where biological capacity must be added to existing tanks.",
      "Sites that cannot tolerate clarifier sludge-recycle complexity.",
      "Industrial wastewater with variable BOD loads.",
    ],
    whereItDoesnt: [
      "Cannot reach reuse-grade water alone — tertiary polishing is required.",
      "Carrier loss is a real failure mode — sieve integrity matters.",
      "Aeration energy intensity is meaningful — 0.6–1.2 kWh/kg BOD removed.",
      "Cold-weather nitrification slows — winter design margins are mandatory.",
    ],
    treatmentChain: {
      intro: "MBBR is the biological core of a wastewater plant — never a complete treatment system on its own.",
      stages: [
      { label: "Pre-treatment", body: "Screening, grit removal, FOG arrestor and equalisation buffer to deliver biologically-feedable flow." },
      { label: "Core stage", body: "Aerated MBBR reactor with engineered carrier fill (typically 40–60% of reactor volume); DO control; biomass thickness managed by aeration shear." },
      { label: "Post-treatment", body: "Lamella clarifier or membrane separation; UV or chlorine disinfection; nutrient finishing where required." },
      { label: "Waste handling", body: "Excess biomass dewatered and disposed under manifest; carrier loss monitored continuously." },
      ],
    },
    applications: [
      "Lodge and estate secondary biological treatment",
      "Brownfield retrofit of biological capacity",
      "Industrial wastewater treatment",
      "Pre-denitrification of high-strength effluents",
    ],
    whereWeUseIt: "We specify MBBR where the brief is compact secondary treatment with shock-load tolerance and retrofit flexibility. Carrier sizing, sieve design, aeration strategy and clarifier or membrane downstream are designed as one package.",
    related: [
      "activated-sludge",
      "membrane-bioreactor",
      "nutrient-reduction",
    ],
    },
  sbr: {
    parent: "wastewater",
    name: "SBR (Sequencing Batch Reactor)",
    short: "Time-cycled biological treatment in a single tank — flexible, robust, and forgiving.",
    whatItIs: "SBR (Sequencing Batch Reactor) is an activated-sludge process that runs all biological treatment stages in a single tank on a time-cycle: fill, react (aerated and anoxic), settle, decant, idle. Each cycle delivers a defined fraction of design treatment, and multiple SBR tanks operate in rotation to manage continuous influent flow.",
    whatItDoes: [
      "Removes 90–98% of BOD/COD on well-sized plants.",
      "Achieves deep nitrogen removal through engineered anoxic phases in the same tank.",
      "Achieves biological phosphorus removal (EBPR) on properly configured cycles.",
      "Tolerates variable flow and load without process upset.",
      "Single-tank architecture simplifies civil works and footprint.",
    ],
    whereItShines: [
      "Sites with strong batch flow profiles (lodges with daily occupancy peaks).",
      "Sites requiring strict effluent BOD, N and P limits.",
      "Decentralised treatment plants where simplicity of architecture matters.",
      "Brownfield upgrades of older oxidation-ditch or trickling-filter plants.",
    ],
    whereItDoesnt: [
      "Continuous high-flow industrial duties — continuous-flow plants are more efficient.",
      "Operating complexity is real — cycle time control demands competent operators or a service contract.",
      "Decant quality is sensitive to settle-phase timing — sloppy operation produces sloppy effluent.",
      "Sludge wasting still requires a proper disposal route.",
    ],
    treatmentChain: {
      intro: "SBR is the biological core of a wastewater plant — the surrounding stages still matter.",
      stages: [
      { label: "Pre-treatment", body: "Screening, grit removal, FOG arrestor, and equalisation buffer sized for the SBR cycle profile." },
      { label: "Core stage", body: "Time-cycled SBR reactor with engineered fill / react / settle / decant / idle phases; DO and ORP feedback control; engineered anoxic phases for nitrogen removal." },
      { label: "Post-treatment", body: "Decant via floating weir; tertiary filtration (sand or cloth); UV or chlorine disinfection; phosphorus polishing where required." },
      { label: "Waste handling", body: "Periodic sludge wasting on cycle; dewatering and disposal under manifest; cycle log retained for regulator audit." },
      ],
    },
    applications: [
      "Lodge and estate secondary biological treatment",
      "Small-town municipal treatment",
      "Industrial batch-flow effluent",
      "Plants requiring strict N and P limits",
    ],
    whereWeUseIt: "We specify SBR where the brief combines variable flow, strict effluent limits and a desire for single-tank architecture. Cycle design, decant strategy and sludge management are part of the engineered package.",
    related: [
      "activated-sludge",
      "membrane-bioreactor",
      "aerobic-biological",
    ],
    },
  "activated-sludge": {
    parent: "wastewater",
    name: "Activated Sludge",
    short: "The proven backbone of large-scale wastewater treatment — when operated by people who understand it.",
    whatItIs:
      "Activated-sludge treatment maintains a suspended biomass (mixed-liquor suspended solids, MLSS) in an aerated reactor. Influent meets the biomass; aerobic micro-organisms consume BOD and convert ammonia to nitrate; the mixed liquor passes to a clarifier where biomass settles and is recycled back to the reactor head.",
    whatItDoes: [
      "Removes 90–95% of BOD and 60–80% of total nitrogen on properly configured plants.",
      "Nitrifies ammoniacal nitrogen to nitrate; denitrifies with engineered anoxic zones.",
      "Removes phosphorus biologically (EBPR) or by chemical precipitation as the discharge target demands.",
      "Recycles biomass for stable performance across flow variation.",
    ],
    whereItShines: [
      "Larger lodges, estates, small towns and industrial sites with stable continuous flow.",
      "Sites requiring strict effluent BOD, COD and nitrogen limits.",
      "Plants where biomass recycle and waste sludge handling can be properly engineered.",
      "Municipal-scale treatment with experienced operating teams.",
    ],
    whereItDoesnt: [
      "Not the right answer for intermittent or peak-only sites — biomass washes out under prolonged low flow.",
      "Operating complexity is meaningful — needs trained operators or a competent service contract.",
      "Footprint is larger than MBBR or MBR for equivalent capacity.",
      "Waste-sludge production is significant and demands an engineered disposal route.",
    ],
        treatmentChain: {
      intro: "Activated sludge is operationally complex — preceded by mechanical conditioning and followed by polishing or disinfection.",
      stages: [
      { label: "Pre-treatment", body: "Inlet screening, grit removal, primary settlement, FOG arrestor, equalisation buffer." },
      { label: "Core stage", body: "Aerated bioreactor with mixed-liquor suspended solids (MLSS) at design concentration; engineered anoxic zones for denitrification; chemical phosphorus precipitation where consent demands it." },
      { label: "Post-treatment", body: "Secondary clarifier; tertiary filtration (sand or cloth media); disinfection (UV or chlorine) before discharge or reuse." },
      { label: "Waste handling", body: "Return activated sludge (RAS) recycled to reactor head; waste activated sludge (WAS) dewatered and disposed under manifest." },
      ],
    },
    applications: ["Municipal wastewater plants of all sizes", "Industrial biological treatment", "Conventional package plants"],
    whereWeUseIt: "We deploy classic activated sludge on continuous-flow sites with strict discharge targets and on plants with operator coverage capable of supporting the process. For decentralised, intermittent or unattended sites, MBBR or SBR is usually a better fit.",
    related: ["aerobic-biological", "mbbr", "sbr"],
  },
  "membrane-bioreactor": {
    parent: "wastewater",
    name: "Membrane Bioreactors (MBR)",
    short: "Discharge-grade effluent in half the footprint — and reuse-ready without a tertiary stage.",
    whatItIs:
      "An MBR couples aerobic biological treatment with ultrafiltration membranes in place of a settling clarifier. The membranes physically separate treated water from the biomass at a nominal pore size of about 0.04 µm, producing a clear, low-solids effluent suitable for direct reuse or polishing.",
    whatItDoes: [
      "Removes >99% of suspended solids and provides ≥4-log barrier to bacteria and protozoa.",
      "Reduces BOD/COD to single-digit mg/L on well-operated plants.",
      "Cuts plant footprint by 40–60% vs activated sludge for the same capacity.",
      "Produces effluent suitable for direct landscape, agricultural or non-potable reuse with minimal further treatment.",
    ],
    whereItShines: [
      "High-end lodges and estates with strict footprint and reuse demands.",
      "Sites where treated effluent must irrigate guest-facing landscape or fill water features.",
      "Industrial applications discharging to sensitive receiving environments.",
      "Where a clarifier cannot physically fit but biological capacity is required.",
    ],
    whereItDoesnt: [
      "Energy cost is higher than activated sludge — aeration and membrane scour dominate.",
      "Membranes need scheduled chemical cleaning (CIP); operator discipline is non-negotiable.",
      "Fats, oils and fibres ruin MBR membranes — fine screening upstream is mandatory.",
      "Replacement membrane cost is real and must be modelled in lifecycle economics.",
    ],
        treatmentChain: {
      intro: "An MBR couples biology and membrane separation in one stage. The membranes change everything that comes before and after.",
      stages: [
      { label: "Pre-treatment", body: "Fine inlet screening (sub-1 mm) is non-negotiable to protect the membranes; FOG arrestor; equalisation buffer." },
      { label: "Core stage", body: "Aerobic bioreactor with submerged or side-stream UF membranes (typically 0.04 µm). Mixed liquor is filtered through the membrane wall; permeate passes; biomass stays." },
      { label: "Post-treatment", body: "Direct reuse or polishing UV; nutrient finishing (chemical phosphorus, post-denitrification) against the consent." },
      { label: "Waste handling", body: "Membrane CIP cleaning chemistry collected and neutralised; WAS dewatered; reject volume on CIP cycles disposed under chemical-waste manifest." },
      ],
    },
    applications: ["Water-reuse projects", "Sites with very limited footprint", "Hospitality and resort with reuse mandates", "High-strength industrial wastewater"],
    whereWeUseIt: "We specify MBR where footprint, reuse quality or discharge compliance forces it — premium lodges, water-stressed estates, sites pursuing closed-loop reuse. Membranes are tier-one OEMs (GE/Suez, Toray, Mitsubishi, Memstar), and CIP regime, screening, blower sizing and replacement-membrane budget are part of the design pack.",
    related: ["mbbr", "sbr", "ultrafiltration"],
  },
  "nutrient-reduction": {
    parent: "wastewater",
    name: "Nutrient Reduction & Final Polishing",
    short: "When the receiving water can't take what a basic plant produces.",
    whatItIs:
      "Nutrient reduction is the targeted removal of nitrogen and phosphorus to meet discharge or reuse limits. Nitrogen is removed biologically via nitrification (NH₃ → NO₃⁻) followed by denitrification in an anoxic zone (NO₃⁻ → N₂↑). Phosphorus is removed by enhanced biological phosphorus removal (EBPR) or by chemical precipitation with iron or aluminium salts.",
    whatItDoes: [
      "Reduces total nitrogen to <10–15 mg/L on well-configured plants.",
      "Reduces total phosphorus to <1–2 mg/L (chemical) or <0.5 mg/L (combined chemical + EBPR).",
      "Prevents eutrophication of downstream rivers, dams and estuaries.",
      "Achieves nutrient targets required for irrigation reuse and sensitive discharge consents.",
    ],
    whereItShines: [
      "Sites discharging to nutrient-sensitive rivers, wetlands or estuaries.",
      "Reuse applications irrigating turf, ornamental planting or food crops.",
      "Discharges into Special Areas of Environmental Significance under DWS authorisation.",
      "Sites where receiving water already shows eutrophication symptoms.",
    ],
    whereItDoesnt: [
      "Adds capital, footprint, energy and chemical cost — not specified where the discharge consent does not demand it.",
      "Cold-weather nitrification slows; design margins for winter are mandatory.",
      "Chemical phosphorus removal generates additional sludge that needs handling.",
      "EBPR is sensitive to influent COD:N:P ratio — design must verify against actual influent.",
    ],
        treatmentChain: {
      intro: "Nutrient reduction is a layer added onto a biological plant — never a stand-alone stage.",
      stages: [
      { label: "Pre-treatment", body: "Full primary and secondary treatment to deliver biologically-stable feed to the nutrient-reduction stage." },
      { label: "Core stage", body: "Engineered anoxic and aerobic zones for biological nitrogen removal; chemical phosphorus precipitation (alum or ferric) and/or EBPR; tertiary denitrification filter where deep N removal is required." },
      { label: "Post-treatment", body: "Clarifier or membrane separation; tertiary filtration; UV or chlorine disinfection." },
      { label: "Waste handling", body: "Chemical sludge from phosphorus precipitation dewatered and disposed; nitrate-reduction backwash to head of works." },
      ],
    },
    applications: ["Discharge to sensitive water bodies", "Reuse-quality effluent", "Green Drop compliance for municipal plants"],
    whereWeUseIt: "We engineer nutrient reduction wherever the discharge consent, receiving water or reuse target demands it. The choice between EBPR, chemical precipitation, post-denitrification filters and combinations is made against the analytical data and the consent numbers, not against a default.",
    related: ["sbr", "membrane-bioreactor"],
  },

  // ─── POOL & SPA ──────────────────────────────────────────────────────
  "afm-media": {
    parent: "pool-spa",
    name: "AFM® (Activated Filter Media)",
    short: "Crystal-clear pool water with 30–50% less chemistry.",
    whatItIs:
      "Activated Filter Media (AFM) is an engineered glass-based filter medium that replaces silica sand in pressure-filter vessels. The grains carry a negative surface charge that bio-resists and helps adsorb fine colloids at lower head loss than sand, with a nominal filtration cutoff of 4–10 µm depending on grade.",
    whatItDoes: [
      "Filters to roughly 4–10 µm — substantially finer than silica sand (~25 µm).",
      "Reduces chlorine demand by 30–50% on most pool installations.",
      "Self-sterilises and inhibits bio-film formation on the medium itself.",
      "Lasts the life of the pressure vessel without recharging or replacement.",
    ],
    whereItShines: [
      "Premium commercial pools, spas and wellness facilities.",
      "Pools targeting low free-chlorine residuals and minimal disinfection by-products.",
      "Sites where backwash water-loss must be minimised.",
      "Replacement of existing sand media without changing the pressure vessel.",
    ],
    whereItDoesnt: [
      "Not a disinfection step — chlorine, UV or ozone is still required for sanitation.",
      "Initial purchase cost is higher than sand — recovered over 3–5 years through chemistry savings.",
      "Performance still depends on correct backwash velocity and filtration rate.",
      "Not a fix for under-circulated or hydraulically-broken pools.",
    ],
        treatmentChain: {
      intro: "AFM is one stage in a managed pool. Circulation, chemistry and disinfection all live alongside it.",
      stages: [
      { label: "Pre-treatment", body: "Skimmer baskets and hair-and-lint pots ahead of the pump to remove gross solids and fibres." },
      { label: "Core stage", body: "Pressure-filter vessel charged with AFM at design backwash and filtration velocity, sized against the pool turnover." },
      { label: "Post-treatment", body: "Disinfection (salt-chlorination, UV or ozone hybrid); ORP/pH feedback chemistry control; heater and balance dosing." },
      { label: "Waste handling", body: "Backwash volumes routed to a discharge point with consent; AFM itself is not replaced — the vessel charge lasts its design life." },
      ],
    },
    applications: ["Commercial swimming pools", "Hotel and lodge wellness pools", "Spa and hydrotherapy circuits", "Drinking-water polishing in some installations"],
    whereWeUseIt: "We standardise on AFM on every commercial pool we sign. The chemistry savings, the lower chlorine residual and the absence of media-replacement cycles all pay back inside 36 months on most lodges and hotels we measure.",
    related: ["media-filtration", "uv-disinfection"],
  },
  "salt-chlorination": {
    parent: "pool-spa",
    name: "Salt Chlorination",
    short: "No chlorine deliveries, no chemical handling — just water that disinfects itself.",
    whatItIs:
      "A salt-chlorination cell generates free chlorine on-site by electrolysing a low concentration of dissolved salt (typically 3 000–4 000 mg/L NaCl) in the pool circulation flow. The chlorine generated is identical to dosed chlorine; the spent chloride converts back to salt as residual is consumed, making the process effectively closed-loop within the pool.",
    whatItDoes: [
      "Generates free chlorine on demand from dissolved salt — no chlorine handling on site.",
      "Maintains an even residual through circulation; over-shoot peaks are eliminated.",
      "Lower chloramine and combined-chlorine levels than batch dosing on most installations.",
      "Integrates with ORP/pH control for hands-off chemistry management.",
    ],
    whereItShines: [
      "Lodges, villas and hotels avoiding on-site liquid chlorine deliveries.",
      "Premium pools targeting a gentler swim experience with lower chloramine.",
      "Sites where automated chemistry is critical (resident-and-guest models).",
      "Indoor pools where chloramine off-gassing is a guest-comfort concern.",
    ],
    whereItDoesnt: [
      "Salinity is brackish — incompatible pump and heater materials must be selected accordingly.",
      "Cell life is finite — replacement on a 5–10 year schedule is part of the lifecycle cost.",
      "Calcium scale on the cell needs periodic acid cleaning.",
      "Not a stand-alone disinfection system on heavily-loaded commercial pools — pair with UV or ozone.",
    ],
        treatmentChain: {
      intro: "Salt chlorination replaces the dosing skid, not the filtration. Everything else in the pool stays.",
      stages: [
      { label: "Pre-treatment", body: "Filtration (AFM or sand) ahead of the salt cell to protect the electrodes from solids fouling." },
      { label: "Core stage", body: "Salt-chlorination cell sized against bather load, with ORP and pH feedback control. Chlorine produced on demand from dissolved NaCl." },
      { label: "Post-treatment", body: "Optional UV polish for low-residual operation; cyanuric-acid stabilisation outdoors; periodic shock-dosing for combined-chlorine breakpoint." },
      { label: "Waste handling", body: "Calcium scale on the cell removed via scheduled acid clean; cell replaced on 5–10 year cycle as part of lifecycle cost." },
      ],
    },
    applications: ["Residential pools", "Luxury hospitality pools", "Spa circuits where chlorine handling is undesirable"],
    whereWeUseIt: "We deploy salt chlorination on most lodge and villa pools where on-site liquid-chlorine handling is undesirable. Cell sizing is matched to bather load and pool turnover, not to the catalogue rating; ORP and pH feedback control are non-optional on commercial duty.",
    related: ["uv-disinfection", "ozone"],
  },
  "vsd-pumps": {
    parent: "pool-spa",
    name: "Variable Speed Drive (VSD) Pumps",
    short: "The pump that listens to the building — not the brochure.",
    whatItIs:
      "Variable-Speed Drive (VSD) pumps modulate motor RPM in real time to deliver the actual demand of the system rather than running at fixed flow and bleeding excess to waste. Modern VSDs (Grundfos CUE, Danfoss VLT, ABB ACS) integrate with pressure or flow feedback and offer soft-start, soft-stop and remote-control protocols.",
    whatItDoes: [
      "Saves 25–45% of pumping energy vs fixed-speed on typical hospitality and estate duties.",
      "Extends bearing, seal and impeller life by reducing thermal and mechanical cycling.",
      "Holds setpoint pressure or flow with ±5% accuracy across full demand range.",
      "Provides Modbus/BACnet integration for site-wide telemetry and control.",
    ],
    whereItShines: [
      "Lodge and estate booster sets serving variable occupancy.",
      "Irrigation main lines where zone count drives flow variability.",
      "Process water duties with stop-start cycles (CIP, filling, washdown).",
      "Sites with on-site solar where pump load needs to follow available generation.",
    ],
    whereItDoesnt: [
      "Capital cost is higher than fixed-speed — recovered through energy savings, not always within the first year.",
      "Harmonic distortion on large drives may need passive or active filters depending on the site supply.",
      "Heat dissipation from the drive demands well-ventilated enclosures, especially in roof spaces.",
      "Not a fix for incorrect hydraulic sizing — a VSD on the wrong pump is still the wrong pump.",
    ],
        treatmentChain: {
      intro: "A VSD pump is one element of a pressure or flow system. Setpoint logic, feedback loop, hydraulic design and protection all sit alongside it.",
      stages: [
      { label: "Pre-treatment", body: "Strainer or filter ahead of the pump suction; air-eliminator on long suction runs; flooded suction wherever the duty demands it." },
      { label: "Core stage", body: "VSD pump unit (Grundfos, Wilo, Pentair) with motor, drive and feedback transducer; setpoint logic in the controller; soft-start and ramp protection." },
      { label: "Post-treatment", body: "Pressure-vessel storage for short-cycle suppression; surge protection; isolation, NRV and gauge package." },
      { label: "Waste handling", body: "Drive-room ventilation and heat extraction; harmonic mitigation where the supply demands it; scheduled belt, bearing and seal replacement under preventive maintenance." },
      ],
    },
    applications: ["Pool and spa circulation", "Booster sets for buildings", "Irrigation pump stations", "Domestic constant-pressure systems"],
    whereWeUseIt: "VSDs are the default on every commercial pumping duty we deliver. Drive selection, harmonic strategy and feedback protocol are part of the design pack — not assumed from a catalogue model number.",
    related: ["afm-media", "salt-chlorination"],
  },

  // ─── PUMPS ───────────────────────────────────────────────────────────
  "booster-sets": {
    parent: "pumps",
    name: "Booster Sets — Duty/Standby/Redundant",
    short: "Stable pressure to the last guest tap — even on a full house.",
    whatItIs:
      "A booster set is a multi-pump, common-manifold installation that maintains a pressure or flow setpoint to a building or site. Pumps share duty automatically; standby pumps take over on duty-pump failure; staging logic brings additional pumps online as demand rises. Modern sets are VSD-driven with PLC or smart-controller logic.",
    whatItDoes: [
      "Delivers stable network pressure (typically ±0.2 bar) across full demand range.",
      "Provides automatic duty-standby fail-over with no operator intervention.",
      "Stages pumps efficiently across the demand curve, minimising energy per m³.",
      "Integrates with site telemetry for remote monitoring and alarm push.",
    ],
    whereItShines: [
      "Hotels, lodges and apartment blocks with multiple floors and simultaneous demand.",
      "Estates with long reticulation runs and zoned pressure targets.",
      "Commercial sites where mains pressure is insufficient or unreliable.",
      "Sites that cannot tolerate pressure failure during peak occupancy.",
    ],
    whereItDoesnt: [
      "Wrong tool for single-tap or low-demand applications — a single VSD pump is more economical.",
      "Manifold pressure-loss budget must be designed in — under-sized headers throttle the whole set.",
      "Standby pumps need scheduled exercise to verify duty rotation works when called upon.",
      "Acoustic load can be significant — plant room location and isolation are design considerations.",
    ],
        treatmentChain: {
      intro: "A booster set is a multi-pump system with logic — never just two pumps on a header.",
      stages: [
      { label: "Pre-treatment", body: "Common-suction strainer, isolation valves on each pump, manifold sized for the worst-case duty pressure-loss." },
      { label: "Core stage", body: "Multiple VSD pumps on common manifold, staged by PLC or smart-controller logic; duty/standby/assist sequence; alarm-mapped to telemetry." },
      { label: "Post-treatment", body: "Bladder accumulator for short-cycle suppression; site-wide distribution; pressure-zone monitoring downstream." },
      { label: "Waste handling", body: "Drain points on each pump and the manifold; sump pump where the plant room is below grade; flushing procedures on commissioning and post-CIP." },
      ],
    },
    applications: ["Hotel, lodge and estate boosting", "Industrial process water supply", "Irrigation main-line pressurisation", "Fire pump duty-standby sets"],
    whereWeUseIt: "We specify booster sets wherever a single pump cannot honour the demand profile or the resilience requirement. Pump count, staging logic, manifold sizing, alarm interface and remote-control protocol are part of the engineered design.",
    related: ["vsd-pumps", "submersible-pumps"],
  },
  "submersible-pumps": {
    parent: "pumps",
    name: "Submersible & Borehole Pumps",
    short: "The pump you can trust in the place you'll never see.",
    whatItIs:
      "Submersible pumps sit below the water surface — in boreholes, sumps, lift stations or reservoirs — coupling pump and motor in a sealed unit. Borehole pumps are typically multi-stage centrifugals; sump and dewatering pumps are typically single-stage centrifugals; sewage pumps use vortex or chopper impellers.",
    whatItDoes: [
      "Lifts water from depth (boreholes, deep sumps, lift stations) without cavitation.",
      "Runs cooled by surrounding water — no above-ground heat dissipation problem.",
      "Self-priming by virtue of being submerged — no priming complications on start-up.",
      "Available in stainless and duplex variants for aggressive, abrasive or saline water.",
    ],
    whereItShines: [
      "Borehole abstraction — every borehole we develop runs on a submersible.",
      "Lift stations and lower-floor sewage transfer.",
      "Reservoir transfer and inter-tank pumping.",
      "Flood-prone basement and sump dewatering.",
    ],
    whereItDoesnt: [
      "Service requires the pump to be pulled — non-trivial on deep installations.",
      "Cable splices and motor seals are weak points if installation is sub-standard.",
      "Sand and abrasion shorten motor life on poorly-developed boreholes.",
      "Not suited to fluctuating water levels that risk dry-running unless protected.",
    ],
        treatmentChain: {
      intro: "A submersible pump is one stage in a borehole or lift-station system — protection, cabling and motor control are as important as the pump itself.",
      stages: [
      { label: "Pre-treatment", body: "Properly developed borehole with gravel pack and screen; sanitary seal and headworks; intake protection in lift stations." },
      { label: "Core stage", body: "Multi-stage submersible (boreholes) or vortex/chopper submersible (sewage); motor protection panel with dry-run, over-current and thermal trips." },
      { label: "Post-treatment", body: "Pressure tank or storage on borehole duty; wet-well alarm and high-level interlock on sewage; rising-main and surge protection." },
      { label: "Waste handling", body: "Pulling arrangements pre-engineered so service does not require dismantling the headworks; rejected components routed to manufacturer recycling." },
      ],
    },
    applications: ["Borehole abstraction", "Deep-well dewatering", "Sump pump-out", "Mine dewatering"],
    whereWeUseIt: "We specify submersibles for every borehole, lift station and sub-grade sump. Motor protection (dry-run, over-current, thermal), cable sizing, riser pipe selection and pulling-arrangements are part of the install — not catalogue items that arrive separately.",
    related: ["booster-sets", "vsd-pumps"],
  },
  "fire-pumps": {
    parent: "pumps",
    name: "Fire-Suppression Sets",
    short: "The pump you hope you never need — but cannot afford to be wrong about.",
    whatItIs:
      "A fire pump is a dedicated, code-certified pump set sized to deliver the fire-protection design flow at a defined residual pressure. Typically diesel-driven or electric with diesel back-up, certified to UL/FM or equivalent, and tested under standards such as NFPA 20.",
    whatItDoes: [
      "Delivers fire-system design flow (litres/sec) at code-required residual pressure.",
      "Operates independently of municipal supply on a stored reserve.",
      "Tests automatically on a defined schedule with logged results.",
      "Interfaces with the fire-alarm panel for autostart on detection.",
    ],
    whereItShines: [
      "Hotels, lodges and conference venues with sprinkler or hydrant systems.",
      "Sites with municipal supply pressure inadequate for fire duty.",
      "Estates and resorts with multiple buildings on a common fire main.",
      "Industrial sites with code-mandated fire protection.",
    ],
    whereItDoesnt: [
      "Cannot be substituted for a domestic booster — fire pumps are single-purpose and certified.",
      "Diesel installations need fuel supply, exhaust routing and weekly start tests.",
      "Storage reserve volume is dictated by the fire-protection design — assumptions cost lives.",
      "Plant room location and acoustic isolation are non-trivial on lodges with guest proximity.",
    ],
        treatmentChain: {
      intro: "A fire pump is a certified, single-purpose installation — designed, witnessed and tested under code, never improvised.",
      stages: [
      { label: "Pre-treatment", body: "Dedicated fire-reserve storage sized to the fire-protection design; flooded suction; jockey pump for system pressurisation." },
      { label: "Core stage", body: "Code-certified fire-pump set (electric primary, diesel back-up where required) with controller, alarm interface and weekly self-test." },
      { label: "Post-treatment", body: "Hydrant and sprinkler reticulation; isolation valves witnessed by the inspector; alarm-panel integration." },
      { label: "Waste handling", body: "Diesel fuel storage, exhaust routing and weekly start-test residue managed under the fire-protection maintenance contract." },
      ],
    },
    applications: ["Commercial site fire reserves", "Lodge and resort fire-defence", "Industrial fire mains", "Estate-wide fire reticulation"],
    whereWeUseIt: "We deliver fire-pump installations against the SANS 10287 / SABS suite and the project's fire-protection design. Certification, scheduled testing, fuel and battery maintenance are part of the engineered package — not afterthoughts.",
    related: ["booster-sets", "submersible-pumps"],
  },

  // ─── IRRIGATION ──────────────────────────────────────────────────────
  "drip-irrigation": {
    parent: "irrigation",
    name: "Drip & Sub-Surface Drip",
    short: "Water at the root — not in the air, not on the leaf, not on the path.",
    whatItIs:
      "Drip irrigation delivers water in low-rate emitters directly at the root zone of plants. Pressure-compensating emitters (Netafim, Rivulis, Toro) hold output to within ±5% across head differences, allowing precise dosing across slope and zone. Lateral lines are typically polyethylene with integrated or in-line emitters.",
    whatItDoes: [
      "Delivers water at 90–95% application efficiency — significantly above sprinkler systems.",
      "Cuts irrigation water demand by 30–50% on landscaping, orchards and gardens.",
      "Reduces foliage wetting — fewer fungal-disease cycles on guest-facing landscape.",
      "Allows precision fertigation through the same lines for nutrient delivery.",
    ],
    whereItShines: [
      "Lodge and estate landscape, ornamental planting and feature gardens.",
      "Commercial orchards (citrus, macadamia, avocado, sub-tropical fruit).",
      "Drought-sensitive plantings and water-restricted sites.",
      "Subsurface drip on premium sports fields and golf surrounds.",
    ],
    whereItDoesnt: [
      "Filtration is critical — drippers block on un-filtered water within months.",
      "Rodent damage on surface laterals is real in bushveld environments.",
      "Not the right answer for turf areas — pop-ups or rotors cover those zones better.",
      "Initial capital is higher than sprinklers — recovered over the first 2–3 years through water savings.",
    ],
        treatmentChain: {
      intro: "Drip is an emitter network — filtration ahead of it, controllers behind it, flushing in between.",
      stages: [
      { label: "Pre-treatment", body: "Sand and disc filtration sized to the dripper rating; pressure-regulator on the lateral header; flush valves on every laterals." },
      { label: "Core stage", body: "Pressure-compensating drip line (Netafim/Rivulis/Toro) laid to the zone layout, with emitters spaced and rated against the soil and species." },
      { label: "Post-treatment", body: "Zone controller logic with skip-on-rain; flushing programme on a defined schedule; fertigation injection where required." },
      { label: "Waste handling", body: "Flush-cycle water routed to soak or vegetated swale; expired drip line returned to recycler at end of useful life." },
      ],
    },
    applications: ["Commercial agriculture", "Landscape and bed irrigation", "Greenhouse production", "Drought-sensitive landscapes"],
    whereWeUseIt: "We deploy drip on every landscape, orchard and ornamental zone that needs precise water delivery. Filtration (sand + disc + screen), pressure compensation, valve zoning and flushing-cycle programming are all part of the design — not catalogue selections.",
    related: ["smart-controllers", "micro-jet"],
  },
  "pop-up-sprinklers": {
    parent: "irrigation",
    name: "Pop-Up & Gear-Driven Sprinklers",
    short: "Even coverage on every turf zone — invisible when off, precise when on.",
    whatItIs:
      "Pop-up sprinklers retract flush with the surface when off and rise to spray distribution when on. Rotors (gear-driven, long-throw, 5–15 m radius) and spray heads (fixed pattern, 2–5 m radius) are matched to the zone geometry and the precipitation rate of the planting.",
    whatItDoes: [
      "Distributes water uniformly across turf and groundcover at engineered precipitation rates.",
      "Retracts below grade when idle — invisible to guests and safe for foot traffic.",
      "Pressure-regulated heads hold output to spec across pressure variation in the line.",
      "Smart nozzles deliver matched-precipitation rates across mixed zones for uniform run-times.",
    ],
    whereItShines: [
      "Lodge lawns, hotel grounds, conference-venue landscapes.",
      "Sports turf, golf surrounds and play areas.",
      "Large open areas where drip would be uneconomical.",
      "Zones requiring even coverage and cooling effect on hot days.",
    ],
    whereItDoesnt: [
      "Lower application efficiency than drip — 70–85% on well-designed systems.",
      "Wind drift on exposed sites can compromise coverage and waste water.",
      "Visible spray timing must respect guest experience — early morning or late evening only on lodges.",
      "Not the right tool for narrow beds, planters, hanging baskets — those need drip.",
    ],
        treatmentChain: {
      intro: "Pop-ups are the visible head — but valves, pressure regulation and controller logic make them perform.",
      stages: [
      { label: "Pre-treatment", body: "Screen filtration to protect heads and solenoids; pressure regulator on each zone valve; isolation on every header." },
      { label: "Core stage", body: "Pressure-regulated pop-up heads (rotors or sprays) selected against zone geometry and precipitation matching." },
      { label: "Post-treatment", body: "Smart controller with ET or weather feedback; zone metering; seasonal-adjust by zone." },
      { label: "Waste handling", body: "Drainage at the end of every zone for winterising on frost-sensitive sites; broken heads recycled rather than dumped." },
      ],
    },
    applications: ["Turf and lawn", "Golf greens and fairways", "Broad-area landscape"],
    whereWeUseIt: "Pop-ups cover the lawn-and-turf zones on every lodge and estate brief. Head selection, spacing, precipitation matching and pressure regulation are calculated against the actual zone geometry, not picked from a catalogue.",
    related: ["drip-irrigation", "smart-controllers"],
  },
  "smart-controllers": {
    parent: "irrigation",
    name: "Smart Zone Automation",
    short: "Irrigation that thinks before it runs.",
    whatItIs:
      "Smart irrigation controllers are weather-responsive, soil-moisture-responsive or evapotranspiration-led control units that decide when, where and how long to irrigate. Modern controllers (Hunter Hydrawise, Rain Bird IQ, Toro Lynx) tie into on-site weather stations, soil-moisture probes and remote forecasts to skip unnecessary cycles.",
    whatItDoes: [
      "Skips irrigation cycles when rain is recent or forecast.",
      "Modulates run-times against measured soil moisture or evapotranspiration.",
      "Logs every cycle, zone and volume for water-balance reporting.",
      "Allows remote control from the lodge office or estate manager's phone.",
    ],
    whereItShines: [
      "Estates and lodges with mixed zones requiring zone-specific schedules.",
      "Sites under municipal water restrictions where compliance must be auditable.",
      "Sites with on-site weather stations or moisture-probe arrays.",
      "Operations where the gardener does not live on site and remote control is essential.",
    ],
    whereItDoesnt: [
      "Subscription and connectivity costs are real — on remote sites without cellular, this is non-trivial.",
      "Garbage in, garbage out — bad zone setup makes a smart controller no smarter than a clock.",
      "Sensor calibration matters — uncalibrated probes drift and the controller follows the drift.",
      "Not a fix for hydraulically broken systems with leaks or blocked zones.",
    ],
        treatmentChain: {
      intro: "A smart controller is the brain of the system — its sensors, network and logic all live around it.",
      stages: [
      { label: "Pre-treatment", body: "Weather station, rain sensor and soil-moisture probe deployment, calibrated against ground-truth." },
      { label: "Core stage", body: "Smart controller (Hunter Hydrawise, Rain Bird IQ, Toro Lynx) with zone schedules, ET feedback, weather skip and remote-control capability." },
      { label: "Post-treatment", body: "Per-zone metering for water-balance reporting; alarm-mapping to operator and HidroVerse engineer; monthly water-use review." },
      { label: "Waste handling", body: "Decommissioned controller hardware returned to manufacturer recycling; firmware updates and security patches applied on contract schedule." },
      ],
    },
    applications: ["Estate-wide landscape automation", "Agricultural pivot and drip control", "Sports turf"],
    whereWeUseIt: "We specify smart controllers on every estate-grade irrigation install. Weather, soil-moisture and ET feedback are paired with monthly reporting — so the GM sees water used per zone, against budget, against weather, against the same period last year.",
    related: ["drip-irrigation", "telemetry-logging"],
  },
  "micro-jet": {
    parent: "irrigation",
    name: "Micro-Jet & Micro-Spray",
    short: "The right tool for orchards and tree surrounds — between drip and full sprinkler.",
    whatItIs:
      "Micro-jets and mini-sprinklers are low-flow emitters that throw a controlled spray pattern over a defined wetting radius (1–3 m). They sit between drip emitters and full sprinklers in flow rate and coverage; widely used in orchard floors, tree-surround irrigation and screened sites.",
    whatItDoes: [
      "Wets the root zone of trees and orchard floors more uniformly than drip.",
      "Operates at low pressure (1–2 bar) with low energy cost.",
      "Offers a frost-protection wetting pattern on susceptible crops on cold nights.",
      "Provides easy visual inspection — blockages and faults are immediately visible.",
    ],
    whereItShines: [
      "Citrus and sub-tropical orchards (avocado, macadamia, mango).",
      "Tree-surround irrigation on lodge and estate landscape.",
      "Screened, fenced or protected areas where rodent damage on drip is excessive.",
      "Sites with hard water where dripper blockage history is significant.",
    ],
    whereItDoesnt: [
      "Less efficient than drip — 80–88% on most installs.",
      "Wind sensitivity is real on exposed orchard sites.",
      "Wetted area between trees may grow weeds that need separate management.",
      "Not the right answer for premium ornamental gardens — drip is finer and quieter.",
    ],
        treatmentChain: {
      intro: "Micro-jets are a managed network — filtration, pressure regulation and flushing are non-optional.",
      stages: [
      { label: "Pre-treatment", body: "Screen and disc filtration on the lateral header; pressure regulation by zone; flush valves at lateral ends." },
      { label: "Core stage", body: "Micro-jet emitters on stakes or risers, throwing a 1–3 m wetting radius; selection matched to species and spacing." },
      { label: "Post-treatment", body: "Zone controller logic; flushing schedule; fertigation injection where the crop demands it." },
      { label: "Waste handling", body: "Flush-cycle water routed to between-row absorption; expired emitter heads collected and recycled." },
      ],
    },
    applications: ["Shrub and ornamental beds", "Horticulture", "Greenhouse production"],
    whereWeUseIt: "We specify micro-jets on orchards and tree surrounds where the wetting pattern needs to be wider than drip but the precision of full sprinklers is unnecessary. Emitter selection, pressure regulation and filtration are designed against the field analysis.",
    related: ["drip-irrigation", "smart-controllers"],
  },

  // ─── MONITORING ──────────────────────────────────────────────────────
  "lorawan-telemetry": {
    parent: "monitoring",
    name: "LoRaWAN & Cellular Telemetry",
    short: "Where cellular fails, the data still arrives.",
    whatItIs:
      "LoRaWAN telemetry uses a long-range, low-power wireless protocol to connect field sensors (tank levels, flow meters, pressure transmitters, water-quality probes) to a private on-site gateway and onward to the cloud. Multi-kilometre range, multi-year battery life on field sensors, and no cellular dependency.",
    whatItDoes: [
      "Connects field instruments across multi-kilometre estate or reserve footprints.",
      "Runs on battery for 3–7 years on typical sensor duty cycles.",
      "Works in remote sites where cellular coverage is absent or unreliable.",
      "Provides encrypted, low-bandwidth telemetry with high reliability.",
    ],
    whereItShines: [
      "Game reserves, lodges and farms covering large remote footprints.",
      "Borehole networks and reservoir clusters with no fixed-line connectivity.",
      "Sites where cellular roaming or weak signal makes 3G/4G unreliable.",
      "Sub-grade reservoirs and pump houses where cellular fails entirely.",
    ],
    whereItDoesnt: [
      "Not for high-bandwidth duties (video, audio) — LoRa is designed for small packets.",
      "Gateway placement and antenna height drive coverage — surveys are essential.",
      "Sensor calibration still needs scheduled service — LoRa is the transport, not the truth.",
      "Public LoRaWAN networks (TheThingsNetwork) are unreliable for commercial duty — we deploy private gateways.",
    ],
        treatmentChain: {
      intro: "LoRaWAN telemetry is the field layer — sensors, gateway and the cloud platform behind it.",
      stages: [
      { label: "Pre-treatment", body: "Site coverage survey, gateway placement and antenna height design for the actual radio environment." },
      { label: "Core stage", body: "Private LoRaWAN gateway with signed firmware; battery-powered field sensors on flow, pressure, level and water-quality duties." },
      { label: "Post-treatment", body: "Cloud platform with role-based access, alarm engine, dashboards and compliance reporting." },
      { label: "Waste handling", body: "Sensor batteries on a documented replacement schedule, returned to a registered recycler at end of life." },
      ],
    },
    applications: ["Remote tank levels", "Far-flung pump status", "Borehole telemetry", "Agriculture soil-moisture and ET stations"],
    whereWeUseIt: "Our default field-telemetry layer on every remote and large-footprint site we monitor. Private gateways, signed firmware, encrypted payload, and a published service contract on coverage and uptime.",
    related: ["telemetry-logging", "pressure-zone-monitoring"],
  },
  "telemetry-logging": {
    parent: "monitoring",
    name: "Telemetry & Data Logging",
    short: "The plant's memory — and your defence in any audit.",
    whatItIs:
      "The telemetry logging stack captures, time-stamps and stores every operational metric from every instrument on every plant we maintain. Data sits in a time-series database, surfaces in role-aware dashboards, and feeds the alarm engine, the compliance report generator and the monthly performance review.",
    whatItDoes: [
      "Captures flow, pressure, level, water quality, energy and consumable status continuously.",
      "Stores raw and normalised data for the plant lifetime with auditable provenance.",
      "Surfaces performance dashboards tailored to GM, engineer, operator and regulator views.",
      "Auto-generates compliance reports — SANS 241, DWS abstraction returns, ESG indicators.",
    ],
    whereItShines: [
      "Every site under HidroVerse Care — used as the single source of truth.",
      "Audits and inspections — DWS, insurer, sustainability reporter, guest auditor.",
      "Performance reviews where trend analysis catches issues before alarms fire.",
      "Cross-site benchmarking on multi-site portfolios.",
    ],
    whereItDoesnt: [
      "Garbage in, garbage out — calibration of the field instruments is what makes the dashboard trustworthy.",
      "Cloud connectivity and data retention costs are real — included in the service tariff, not free.",
      "Not a substitute for site visits — telemetry observes, technicians intervene.",
      "Data export and API access need to be in the contract, not a battle later.",
    ],
        treatmentChain: {
      intro: "Telemetry logging is the data backbone — sensors at one end, dashboards and compliance reports at the other.",
      stages: [
      { label: "Pre-treatment", body: "Calibrated field instruments against traceable standards; verified comms path from instrument to gateway." },
      { label: "Core stage", body: "Time-series database (cloud-hosted within Africa) capturing every reading from every instrument with timestamp and provenance." },
      { label: "Post-treatment", body: "Role-aware dashboards; alarm engine with multi-channel notification; auto-generated compliance reports (SANS 241, DWS, ESG)." },
      { label: "Waste handling", body: "Data retention policy aligned to regulatory minimums; secure archival and customer-controlled export." },
      ],
    },
    applications: ["Compliance reporting", "Predictive maintenance", "Performance trending", "Insurance-grade incident reconstruction"],
    whereWeUseIt: "On every site we maintain. The dashboard is your view; the underlying time-series and the auto-generated compliance pack is what defends you in an audit.",
    related: ["lorawan-telemetry", "pressure-zone-monitoring"],
  },
  "pressure-zone-monitoring": {
    parent: "monitoring",
    name: "Pressure-Zone Alerting",
    short: "Leaks announce themselves — before anyone sees the wet patch.",
    whatItIs:
      "Pressure-zone monitoring instruments the distribution network into hydraulic zones, each with continuous pressure and flow logging at the entry point. Zone behaviour is benchmarked against the same period last year, the same period last week, and the predicted demand — so leaks and breaks announce themselves before anyone notices the wet patch.",
    whatItDoes: [
      "Detects creeping leaks before they become visible on the lawn or in the basement.",
      "Identifies illegal connections and unmetered offtakes by reconciling zone balance.",
      "Triggers immediate alerts on rapid pressure drops indicative of a burst main.",
      "Provides per-zone consumption metrics for water-balance and ESG reporting.",
    ],
    whereItShines: [
      "Large estates and resorts with long reticulation runs and many offtakes.",
      "Lodges with hidden underground mains where surface-leak observation is impossible.",
      "Sites with a documented history of unexplained water-loss.",
      "Sites pursuing certified water-management benchmarks (LEED, EDGE, Green Star).",
    ],
    whereItDoesnt: [
      "Requires accurate zone-isolation — boundary valves that don't close make the data meaningless.",
      "Static-pressure baseline must be established before alarms can be calibrated.",
      "Field meters need scheduled verification — drift produces false alarms.",
      "Not a replacement for a leak-detection survey — but it makes the survey targeted.",
    ],
        treatmentChain: {
      intro: "Zone monitoring is the instrumentation layer — boundaries, baselines and the alarm engine that turns data into action.",
      stages: [
      { label: "Pre-treatment", body: "Network audit to confirm zone boundaries; valve verification to ensure isolation is real; instrument selection per zone." },
      { label: "Core stage", body: "Pressure and flow transmitters at each zone entry; continuous logging via LoRaWAN or cellular; baselining of normal behaviour." },
      { label: "Post-treatment", body: "Alarm engine triggering on rapid pressure drop or slow-leak trend; targeted leak-detection survey when alarms localise the loss." },
      { label: "Waste handling", body: "Instrument drift checks on schedule; replacement transmitters returned to manufacturer recycler." },
      ],
    },
    applications: ["Estate reticulation", "Hotel riser monitoring", "Lodge multi-building distribution", "Municipal small-network"],
    whereWeUseIt: "Default on every estate and large-footprint site under HidroVerse Care. We zone the network at install, instrument the boundaries, baseline normal behaviour, and run the alarm engine against the live data.",
    related: ["telemetry-logging", "lorawan-telemetry"],
  },

  // ─── HARVESTING ──────────────────────────────────────────────────────
  "borehole-intake": {
    parent: "harvesting",
    name: "Borehole Intake & Abstraction",
    short: "A drilled hole only becomes an asset when the abstraction is engineered.",
    whatItIs:
      "A borehole intake is the engineered abstraction system that turns a drilled hole into a sustainable water source: casing, well screen, gravel pack, sanitary seal, submersible pump, motor protection and abstraction monitoring. Yield is verified by step-discharge and constant-discharge testing before any equipment is sized.",
    whatItDoes: [
      "Abstracts water from the targeted aquifer at the proven sustainable yield.",
      "Protects the borehole from surface contamination via sanitary seal and headworks.",
      "Logs abstraction volume continuously for water-use authorisation compliance.",
      "Monitors water level and drawdown to protect against over-abstraction.",
    ],
    whereItShines: [
      "Off-grid and water-stressed sites with no reliable surface supply.",
      "Lodges, farms and estates where independence from municipal supply is strategic.",
      "Sites with aquifers tested at sustainable yield matching the demand.",
      "Multi-borehole arrays providing redundancy and yield distribution.",
    ],
    whereItDoesnt: [
      "Boreholes need a National Water Act authorisation above the General Authorisation thresholds.",
      "Sustainable yield is finite — over-abstraction collapses the aquifer and the lodge with it.",
      "Drilling and equipping cost is real and recovery on capital takes years.",
      "Borehole chemistry is not free water — purification is invariably required.",
    ],
        treatmentChain: {
      intro: "A borehole is one source in a multi-source site water plan — the abstraction is one stage of a longer treatment chain.",
      stages: [
      { label: "Pre-treatment", body: "Step-discharge and constant-discharge testing to verify sustainable yield; casing, screen and gravel-pack design against the geology." },
      { label: "Core stage", body: "Submersible pump sized to sustainable yield; motor protection panel; abstraction metering; sanitary seal and headworks." },
      { label: "Post-treatment", body: "Pre-treatment chemistry, particulate removal, membranes and disinfection — the full purification train against the borehole chemistry." },
      { label: "Waste handling", body: "Test-pumping water disposed under controlled discharge; downhole equipment returned to manufacturer for refurbishment or recycling." },
      ],
    },
    applications: ["Lodge, estate, farm, residential, municipal supply", "Industrial process water", "Mine dewatering and process supply"],
    whereWeUseIt: "We integrate the borehole intake into the site water plan, not as a stand-alone asset. Test pumping, equipping, abstraction monitoring, sanitary protection and the downstream treatment train are designed as one package.",
    related: ["submersible-pumps", "rainwater-capture"],
  },
  "rainwater-capture": {
    parent: "harvesting",
    name: "Rainwater Capture Systems",
    short: "Every roof is a water source — if you treat it like one.",
    whatItIs:
      "Rainwater capture systems collect runoff from roofs and clean catchment surfaces, divert first-flush load, screen leaves and debris, and store water in sized tanks for later use. Modern installs include first-flush diverters, mesh leaf screens, calmed inlets and bypass overflows engineered against the catchment-area design rainfall.",
    whatItDoes: [
      "Captures roof and clean-catchment runoff for storage and re-use.",
      "Diverts the polluted first-flush volume away from the storage tank.",
      "Reduces dependence on borehole or municipal supply during the wet season.",
      "Provides emergency reserve buffer during dry-season abstraction restrictions.",
    ],
    whereItShines: [
      "High-rainfall coastal and escarpment sites with consistent wet-season volume.",
      "Lodges and estates with substantial clean-roof catchment area.",
      "Sites pursuing certified water-management benchmarks (LEED, EDGE, Green Star).",
      "Drinking-water augmentation for high-occupancy peak-season periods.",
    ],
    whereItDoesnt: [
      "Won't carry a high-demand site through a long dry season on its own.",
      "Cleanliness depends on roof material — asbestos, certain coatings and bird-heavy roofs need additional treatment.",
      "Storage volume vs catchment area must be calculated honestly — undersized either side wastes capital.",
      "Rainwater is not potable as captured — treatment is always required for drinking duty.",
    ],
        treatmentChain: {
      intro: "Rainwater capture is a primary source, sized against the catchment, the rainfall and the demand. Treatment downstream is non-optional.",
      stages: [
      { label: "Pre-treatment", body: "Mesh leaf screens on the gutters; first-flush diverters sized against the historical worst rainfall event; calmed inlet to the storage tank." },
      { label: "Core stage", body: "Sectional or moulded storage tank sized against catchment area and design rainfall; bermed, screened or built into the architecture to disappear." },
      { label: "Post-treatment", body: "Filtration, disinfection and (for potable duty) the full purification train; smart blending logic where rainwater shares storage with other sources." },
      { label: "Waste handling", body: "Tank overflow routed to soakaway or rainwater swale; first-flush volume routed to landscape rather than tank." },
      ],
    },
    applications: ["Residential drought resilience", "Lodge supplementary supply", "Estate landscape irrigation", "Agriculture supplementary water"],
    whereWeUseIt: "We integrate rainwater capture into the site water balance as a real source — sized against the design rainfall and the demand, with first-flush diverters and a calmed inlet that protects the storage from disturbance.",
    related: ["borehole-intake", "surface-water-intake"],
  },
  "surface-water-intake": {
    parent: "harvesting",
    name: "Surface-Water Intake",
    short: "Where a river runs through your site — turn it into your supply.",
    whatItIs:
      "Surface-water intake is the engineered abstraction from a river, dam or impoundment — including foot valves, intake screens, riser piping, low-level cut-out and abstraction metering. Position, depth, screening and seasonal variability are all engineered into the design.",
    whatItDoes: [
      "Abstracts water from river, dam or impoundment as a primary or supplementary source.",
      "Screens out coarse debris, fish and organic matter at the intake.",
      "Protects the pump from low-level dry-running and silt ingress.",
      "Logs abstraction volume for water-use authorisation compliance.",
    ],
    whereItShines: [
      "Lodges with riparian water rights on perennial rivers.",
      "Sites supplementing borehole supply during dry-season pressure on the aquifer.",
      "Estates with on-site dams or impoundments as a primary supply.",
      "Industrial and agricultural process water duties.",
    ],
    whereItDoesnt: [
      "River and dam abstraction needs a Water Use Licence above General Authorisation thresholds.",
      "Seasonal flow variation must be designed for — design fails on the dry-month low flow.",
      "Surface water is heavily contaminated by design assumption — substantial treatment is mandatory.",
      "Floods and debris events damage poorly-anchored intake structures.",
    ],
        treatmentChain: {
      intro: "Surface-water intake is a heavily contaminated source by design assumption. Pre-treatment and treatment dominate the chain.",
      stages: [
      { label: "Pre-treatment", body: "Properly anchored intake with screens, foot valve, low-level cut-out, abstraction metering and (where seasonal) intake-depth flexibility." },
      { label: "Core stage", body: "Lift pump (typically submersible or vertical turbine) sized against worst-case dry-season head; surge protection on the rising main." },
      { label: "Post-treatment", body: "Coarse screening, lamella clarification, media filtration, membrane stage and disinfection — surface water demands the full multi-barrier train." },
      { label: "Waste handling", body: "Backwash and CIP volumes managed under the discharge consent; abstraction metering data submitted to DWS on schedule." },
      ],
    },
    applications: ["Lodge river abstraction", "Agricultural dam supply", "Municipal raw-water intake"],
    whereWeUseIt: "We integrate surface-water intake into the site water-source matrix with proper anchoring, screening, low-level protection and abstraction metering. Cross-source switching (rainwater first, surface next, borehole or municipal as backup) is engineered into the controller logic.",
    related: ["rainwater-capture", "sand-separators"],
  },
  "coastal-intake": {
    parent: "harvesting",
    name: "Coastal & Saline Intake",
    short: "The first stage of every coastal desalination plant — gets this wrong and the rest is irrelevant.",
    whatItIs:
      "A coastal intake delivers seawater or estuarine water to a desalination plant via a sub-surface beach well, an open-sea intake with screens, or a horizontal directional-drilled (HDD) galvanised structure. Selection is led by site geomorphology, marine biology and the protection the intake offers against fouling.",
    whatItDoes: [
      "Abstracts seawater at the cleanliness profile the desalination plant was designed for.",
      "Beach-well intakes provide natural pre-filtration through the beach sand.",
      "Open-sea intakes deliver consistent salinity at depth, isolated from surf-zone variability.",
      "HDD intakes reduce surface footprint and visual impact on sensitive coastlines.",
    ],
    whereItShines: [
      "Coastal desalination plants for lodges, resorts and small coastal towns.",
      "Sites where surface intake would be vulnerable to swell, storms and surf-zone fouling.",
      "Environmentally-sensitive shorelines requiring minimal visual impact.",
      "Marine vessels and offshore platforms.",
    ],
    whereItDoesnt: [
      "Beach-well capacity is geology-dependent — not all sites deliver useful yield.",
      "Marine bio-fouling on open-sea intakes demands chlorination or shock dosing on a schedule.",
      "Environmental authorisation timelines are non-trivial — pre-engineering before approval is risky.",
      "Storm and swell damage on poorly anchored open intakes is a real lifecycle risk.",
    ],
        treatmentChain: {
      intro: "Coastal intake is one stage in a desalination package — pre-treatment, RO and concentrate disposal are designed around it.",
      stages: [
      { label: "Pre-treatment", body: "Geotechnical and marine-biology survey; environmental authorisation; intake type selected (beach well, open sea, HDD) against the site." },
      { label: "Core stage", body: "Intake structure built to the design — screening, anchoring, biofouling control and abstraction metering." },
      { label: "Post-treatment", body: "Chlorination, coagulation, UF or DAF to deliver SDI₁₅ below 5 on the membrane feed; full SWRO train downstream." },
      { label: "Waste handling", body: "Concentrate diffuser discharge under marine-environmental authorisation; intake-screen back-flush routed to the diffuser stream." },
      ],
    },
    applications: ["Coastal-lodge desalination", "Island and marine supply", "Industrial coastal sites"],
    whereWeUseIt: "Coastal intakes are designed against the geomorphology, marine biology and environmental authorisation specific to each site. We deliver intake, pre-treatment and the desalination plant as one integrated package.",
    related: ["desalination", "borehole-intake"],
  },
  "smart-blending": {
    parent: "harvesting",
    name: "Smart Transfer & Multi-Source Blending",
    short: "Use the cheapest, cleanest source available — automatically.",
    whatItIs:
      "Smart blending logic prioritises and mixes available water sources — rainwater, borehole, surface, municipal — against cost, quality, availability and storage state. The blend ratio shifts in real time based on instrumentation and the operator's priority profile.",
    whatItDoes: [
      "Prioritises the cheapest and cleanest source available at any moment.",
      "Protects the borehole during dry seasons by drawing from rainwater first.",
      "Switches to municipal or backup automatically when primary sources fall below threshold.",
      "Records source contributions for audit and water-balance reporting.",
    ],
    whereItShines: [
      "Sites with multiple water sources of different costs and qualities.",
      "Estates pursuing certified water-management benchmarks where source provenance matters.",
      "Resilience-led briefs where single-source failure cannot interrupt supply.",
      "Operations targeting visible reduction in municipal consumption.",
    ],
    whereItDoesnt: [
      "Single-source sites have nothing to blend — added complexity without benefit.",
      "Garbage in, garbage out — bad sensor readings produce bad blend decisions.",
      "Quality differences between sources need treatment-train integration, not just valve switching.",
      "Operator override is non-optional — every blending logic must be defeatable in emergency.",
    ],
        treatmentChain: {
      intro: "Smart blending is a controller and a logic layer — the sources and the treatment train still live underneath.",
      stages: [
      { label: "Pre-treatment", body: "Instrumented intake on each source (rainwater, borehole, surface, municipal) with quality and quantity telemetry." },
      { label: "Core stage", body: "Source-prioritisation logic in the controller with priority profile, threshold alarms and operator-override capability." },
      { label: "Post-treatment", body: "Storage compartmentalisation, blended-feed conditioning and the downstream treatment train against the worst-case blended chemistry." },
      { label: "Waste handling", body: "Decommissioned source instrumentation returned to manufacturer recycling; logic audit logs retained for the plant lifetime." },
      ],
    },
    applications: ["Estate and lodge multi-source supply", "Drought-resilient residential", "Agricultural multi-source farms"],
    whereWeUseIt: "We deploy smart blending on every multi-source site we engineer. Source prioritisation logic is encoded in the controller, visible to the operator, logged to telemetry and reviewed quarterly against operating outcomes.",
    related: ["telemetry-logging", "borehole-intake"],
  },
  "storage-partitioning": {
    parent: "harvesting",
    name: "Daily, Peak & Emergency Partitioning",
    short: "Raw, intermediate and treated water never share a tank.",
    whatItIs:
      "Storage partitioning splits raw, intermediate and treated water into separately-contained reservoirs so each volume is fit-for-purpose and cannot contaminate the next. Each compartment has its own level instrumentation, intake protection and discharge interlock.",
    whatItDoes: [
      "Prevents cross-contamination between raw, intermediate and treated water.",
      "Provides hydraulic buffering at each stage of the treatment train.",
      "Allows isolated cleaning or maintenance of one compartment without losing supply from another.",
      "Sized against the worst-case dry period for the catchment.",
    ],
    whereItShines: [
      "Multi-source sites where raw and treated water cannot share a tank.",
      "Sites requiring an emergency potable reserve isolated from operating storage.",
      "Lodges with high-occupancy peaks requiring buffered supply through the treatment train.",
      "Industrial sites with multiple use-classes (potable, process, irrigation, fire reserve).",
    ],
    whereItDoesnt: [
      "Adds footprint and capital — over-spent on simple, single-class water duties.",
      "Multiple-tank installations need careful gravity and ventilation engineering.",
      "Cross-connection prevention requires backflow devices and an enforced testing regime.",
      "Not a substitute for treatment — partitioning isolates volumes, it does not purify them.",
    ],
        treatmentChain: {
      intro: "Storage partitioning is the architecture of the storage strategy — sized, instrumented and protected.",
      stages: [
      { label: "Pre-treatment", body: "Hydraulic design to determine compartment volumes against worst-case demand and treatment-stage residence times." },
      { label: "Core stage", body: "Separately-contained reservoirs — raw, intermediate, treated, emergency, fire reserve — each instrumented and isolated from the others." },
      { label: "Post-treatment", body: "Cross-connection prevention via backflow devices on every interface; scheduled hygiene cycle on each compartment." },
      { label: "Waste handling", body: "Cleaning chemistry from each compartment collected and disposed; spent inspection results retained in the asset-management record." },
      ],
    },
    applications: ["Every commercial site", "Estate-wide reticulation", "Hospitality multi-building"],
    whereWeUseIt: "Every multi-source site we engineer gets a storage strategy, not a tank. Raw, treated and emergency reserves are separately contained, separately instrumented, and separately protected.",
    related: ["fire-pumps", "smart-blending"],
  },

  // ─── SERVICE ─────────────────────────────────────────────────────────
  "preventive-maintenance": {
    parent: "service",
    name: "Preventive Care & Inspection",
    short: "What separates a plant that lasts a decade from one that lasts a season.",
    whatItIs:
      "Preventive maintenance is the scheduled service programme that keeps a plant inside its design window — filter changes, UV lamp replacement, media top-ups, dosing-chemistry refills, calibration of probes and instruments, and verification of safety interlocks. Each task is on a calendar, not in an operator's memory.",
    whatItDoes: [
      "Replaces consumables (cartridges, lamps, dosing chemistry) on a schedule that beats failure.",
      "Calibrates probes and instruments against traceable standards.",
      "Verifies safety interlocks and fail-safe behaviours on a defined cycle.",
      "Logs every visit, every task, every consumable and every reading to a permanent compliance file.",
    ],
    whereItShines: [
      "Every site under HidroVerse Care — without this, the SLA is meaningless.",
      "Sites where unscheduled downtime is unacceptable (lodges, healthcare, food and beverage).",
      "Plants approaching mid-life where degradation acceleration is visible in the data.",
      "Compliance-led sites where the auditor expects a defendable service record.",
    ],
    whereItDoesnt: [
      "Cannot save a plant that was poorly engineered in the first place.",
      "Not a substitute for emergency response — both are needed.",
      "Operator complacency is a real risk if PM becomes routine rather than considered.",
      "Schedule compression to save cost defeats the purpose entirely.",
    ],
        treatmentChain: {
      intro: "Preventive maintenance is the operating discipline that protects every other stage of the treatment chain.",
      stages: [
      { label: "Pre-treatment", body: "Calendar-driven schedule per plant; pre-positioned spares on remote sites; calibration log against traceable standards." },
      { label: "Core stage", body: "Filter changes, UV lamp replacement, dosing-chemistry refills, probe calibration, safety-interlock verification on every visit." },
      { label: "Post-treatment", body: "Visit log, consumable register and condition report in the compliance file; trend review monthly with the engineer." },
      { label: "Waste handling", body: "Spent consumables returned to manufacturer or licensed recycler; chemistry waste manifested under HazWaste." },
      ],
    },
    applications: ["Every HidroVerse installation", "Third-party plant service-takeover", "Industrial plant maintenance"],
    whereWeUseIt: "Every site we maintain. The schedule is published, the consumables are on the contract, and the service log is available for the GM or auditor to see in real time.",
    related: ["membrane-cip", "scheduled-sampling"],
  },
  "critical-response": {
    parent: "service",
    name: "Critical Response Teams",
    short: "On site within four hours — measured, not promised.",
    whatItIs:
      "Critical-response is the contractual commitment to be on site within a defined time after a critical failure — typically 4 hours on commercial plants where a site cannot supply water at any outlet. Pre-positioned spares, on-call technicians and a defined escalation matrix make the SLA real, not theoretical.",
    whatItDoes: [
      "Guarantees on-site arrival within the SLA window for critical failures.",
      "Pre-positions spares and consumables on remote sites to compress response time.",
      "Provides 24/7 telephone triage to determine severity before dispatch.",
      "Triggers service credits automatically when the SLA is missed.",
    ],
    whereItShines: [
      "Lodges and resorts where guest-water failure costs are measured in cancellations.",
      "Healthcare and food-production sites where regulatory consequences are immediate.",
      "Mining and industrial sites where production downtime is the dominant cost.",
      "Long-haul sites where DIY response would take days.",
    ],
    whereItDoesnt: [
      "Cannot defy physics — extreme remote sites with no all-weather access have a longer realistic floor.",
      "Cannot substitute for preventive maintenance — a plant only relying on critical response is failing.",
      "SLA credits are not the same as resilience — design redundancy is still required.",
      "Cost is real and must be paid for in the maintenance tariff — there is no free response.",
    ],
        treatmentChain: {
      intro: "Critical response is one part of the service relationship — preventive maintenance, sampling and telemetry are the others.",
      stages: [
      { label: "Pre-treatment", body: "24/7 triage line; documented escalation matrix; pre-positioned spares on remote sites; alarm-mapping from telemetry." },
      { label: "Core stage", body: "On-site arrival within SLA window; root-cause restoration; service-credit reconciliation if the SLA is missed." },
      { label: "Post-treatment", body: "Post-incident report with corrective actions; trend analysis for repeat-event prevention; updates to the preventive-maintenance schedule." },
      { label: "Waste handling", body: "Failed-component disposal under manifest; lessons-learnt circulated across the maintenance fleet." },
      ],
    },
    applications: ["Lodge & resort operations", "Industrial process water", "Municipal plant support", "Estate-wide site management"],
    whereWeUseIt: "Every HidroVerse Care contract specifies the response SLA in writing, with service-credit terms attached. We track median on-site arrival continuously and publish it to clients.",
    related: ["preventive-maintenance", "telemetry-logging"],
  },
  "scheduled-sampling": {
    parent: "service",
    name: "Scheduled SANS 241 Sampling",
    short: "Audit-ready every month — not just the month before the inspection.",
    whatItIs:
      "Scheduled sampling is the regulated, defendable sampling regime that proves a plant is meeting its compliance target — typically monthly potable sampling through a SANAS-accredited laboratory, against the SANS 241 panel. Sampling points, hold times, chain-of-custody and certificate filing are all part of the regime.",
    whatItDoes: [
      "Samples at compliant points on a defined cycle, with chain-of-custody discipline.",
      "Returns SANAS-accredited certificates that defend the plant against any regulator or auditor.",
      "Trends parameters over time so creeping non-compliance is caught before it crosses a limit.",
      "Triggers alarm and remediation workflow if any parameter trends or breaches.",
    ],
    whereItShines: [
      "Potable plants serving lodges, hotels, estates and small towns under SANS 241 duty.",
      "Reuse plants where the receiving authority requires periodic compliance verification.",
      "Insurance-led briefs where annual reporting is required.",
      "ESG-led briefs where sampling cadence is part of the reporting framework.",
    ],
    whereItDoesnt: [
      "Sampling alone does not make a plant compliant — design and operation do.",
      "Wrong sampling point or wrong frequency produces a false confidence record.",
      "Lab turnaround time means breaches are caught after the fact — pair with telemetry for live trend monitoring.",
      "Cost is real and must be paid for in the service contract — there is no free sampling.",
    ],
        treatmentChain: {
      intro: "Scheduled sampling is the evidence layer of the service contract — preventive maintenance and telemetry are the operational layers.",
      stages: [
      { label: "Pre-treatment", body: "Sampling-point design at plant commissioning; chain-of-custody discipline trained into every technician." },
      { label: "Core stage", body: "Monthly potable sampling at compliant points; courier to SANAS-accredited laboratory; SANS 241 panel returned within published turnaround." },
      { label: "Post-treatment", body: "Certificate filed in compliance pack within 5 business days; trend analysis on a quarterly cycle; alarm if any parameter trends out of spec." },
      { label: "Waste handling", body: "Sample-container disposal per laboratory protocol; archived results retained for the plant lifetime." },
      ],
    },
    applications: ["Private potable water systems", "Lodge, estate, hotel water supply", "Industrial drinking-water provision", "Municipal supply"],
    whereWeUseIt: "Included on the standard HidroVerse Care contract for every potable plant. Sample points, schedule, lab and turnaround are documented; certificates are filed in the compliance pack within 5 business days of result.",
    related: ["preventive-maintenance", "membrane-cip"],
  },
  "membrane-cip": {
    parent: "service",
    name: "Membrane Integrity & CIP",
    short: "Membranes that last five years — not five months.",
    whatItIs:
      "Clean-in-Place (CIP) is the controlled chemical cleaning of RO, UF and MBR membranes — alkaline soak to lift organic and biological fouling, acidic soak to lift mineral scale, sometimes oxidant to lift biofilm. CIP is triggered by normalised performance drift, not by calendar guesswork.",
    whatItDoes: [
      "Restores normalised permeate flow and salt rejection on fouled RO/UF membranes.",
      "Removes biofilm, scale and organic fouling that operational dosing cannot prevent.",
      "Extends membrane life by years on properly executed schedules.",
      "Documents every cleaning cycle for warranty and lifecycle accounting.",
    ],
    whereItShines: [
      "RO plants on dirty or biologically-active feeds.",
      "UF plants approaching transmembrane-pressure trigger thresholds.",
      "MBR systems where membrane fouling drives aeration energy cost.",
      "End-of-life recovery before deciding whether to clean or replace.",
    ],
    whereItDoesnt: [
      "Wrong chemistry damages the membrane permanently — not a DIY exercise.",
      "Cannot fix mechanical damage (telescoping, leaks, glue-line failure).",
      "Excessive CIP frequency is a symptom of upstream pre-treatment failure, not a solution.",
      "CIP waste is a regulated chemical waste stream — disposal route must be in the contract.",
    ],
        treatmentChain: {
      intro: "Clean-in-Place is a chemical maintenance stage triggered by normalised performance drift — not by calendar guesswork.",
      stages: [
      { label: "Pre-treatment", body: "Normalised performance trend analysis; CIP trigger thresholds defined per plant; chemistry and dosage selected against fouling type." },
      { label: "Core stage", body: "Alkaline soak (NaOH at controlled pH and temperature) for organic and biological fouling; acidic soak (citric or HCl) for mineral scale; oxidant where biofilm dominates." },
      { label: "Post-treatment", body: "Post-CIP performance verification; salt-rejection and normalised permeate flow logged; trend reset baseline established." },
      { label: "Waste handling", body: "Spent CIP chemistry neutralised before discharge; volume, pH and temperature recorded for the discharge consent." },
      ],
    },
    applications: ["Every RO, NF, UF and MBR installation", "Industrial membrane plants", "Municipal membrane stations"],
    whereWeUseIt: "We CIP membranes against normalised performance trigger thresholds, not on a fixed calendar. Chemistry, contact time, temperature and disposal route are documented per system and logged on each cycle.",
    related: ["reverse-osmosis", "ultrafiltration", "scheduled-sampling"],
  },

  // ─── SOLAR CLEANING ─────────────────────────────────────────────────
  "robotic-cleaning": {
    parent: "solar-cleaning",
    name: "Autonomous Robotic Cleaning",
    short: "Inspect and clean the tank — without taking the site offline.",
    whatItIs:
      "Robotic and automated cleaning systems — tank robots, reservoir crawlers, pipeline inspection rigs — clean and inspect water-storage infrastructure without taking it offline. Mid-pressure water-jet rotating heads, brush-and-vacuum crawlers and underwater inspection cameras are the typical kit.",
    whatItDoes: [
      "Cleans storage reservoirs while the site stays in operation.",
      "Inspects tank floors, walls and roofs for corrosion, cracking and biofilm.",
      "Captures video and dimensional data for asset-condition reporting.",
      "Reduces tank-cleaning labour cost and confined-space-entry risk dramatically.",
    ],
    whereItShines: [
      "Large municipal and industrial storage reservoirs.",
      "Lodges and estates where draining and refilling tanks is impractical.",
      "Sites with regulatory tank-inspection cycles and limited operational windows.",
      "Reservoirs in service that cannot be safely entered by personnel.",
    ],
    whereItDoesnt: [
      "Small domestic tanks are more economically cleaned manually.",
      "Heavily fouled or damaged tanks may still need drain-down and confined-space entry.",
      "Robotic kit is a capital cost — service contracts typically share the cost across multiple sites.",
      "Operator skill on the robot matters — sub-standard operation produces sub-standard outcomes.",
    ],
        treatmentChain: {
      intro: "Robotic cleaning is an asset-management stage — inspection plus mechanical cleaning, without confined-space entry risk.",
      stages: [
      { label: "Pre-treatment", body: "Asset-condition baseline; tank entry-port survey; robot selection (crawler vs water-jet) against the tank geometry." },
      { label: "Core stage", body: "Robotic crawler or rotating-jet system cleans and inspects floors, walls and roofs; video and dimensional data captured." },
      { label: "Post-treatment", body: "Inspection report; remediation plan for any defects; routine schedule established for follow-up cleaning cycles." },
      { label: "Waste handling", body: "Cleaning effluent removed via dedicated pump-out and disposed under the discharge consent; sludge dewatered and disposed." },
      ],
    },
    applications: ["Off-grid lodge PV arrays", "Commercial rooftop solar", "Large-scale solar farms", "Remote industrial sites"],
    whereWeUseIt: "We deploy robotic tank cleaning and inspection on storage assets where continuous operation matters and traditional drain-and-clean is uneconomical or unsafe.",
    related: ["telemetry-logging"],
  },
};
